// OutsourceCaseDetails.jsx - Responsive with Separated CSS + SweetAlert2
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaArrowLeft, FaDownload, FaFile, FaFilePdf, FaUpload, FaTrash, FaSpinner, FaSync, FaRegCopy, FaTooth, FaFileInvoice, FaFileInvoiceDollar, FaStickyNote, FaPlus, FaTimes, FaCloudUploadAlt, FaCheckCircle, FaTimesCircle, FaEye, FaEdit, FaHistory, FaSave, FaMinus, FaPlusCircle, FaBan, FaExchangeAlt, FaUndo } from 'react-icons/fa';
import { PiImage } from "react-icons/pi";
import Footer from '../../../footer.js';
import { Button, Spinner, Modal, Form, InputGroup, ListGroup, Row, Col, ProgressBar, Badge } from 'react-bootstrap';
import Header from '../../../header/header.js';
import Navvbar from '../../../header/Navvbar.js';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useParams, useNavigate } from 'react-router-dom';
import OutsourceActionButton from '../newCase/case_id/actionButton/OutsourceActionButton.js';
import STLViewer from '../newCase/case_id/caseTable/STLViewer.js';
import { useCurrency } from '../../../../context/currencyContext.js';
import OutsourceInvoiceModal from './OutsourceInvoiceModal.jsx';

// Pricing access hook — hides amounts / disables invoice links when the
// user has no pricing access. Admins get access automatically via the hook.
import usePricingAccess from '../../../../hooks/usePricingAccess.js';

import './outsourceCaseDetails.css';

// ==================== SWEETALERT HELPER FUNCTIONS ====================
const showSuccessAlert = (title, text = '') => {
  Swal.fire({
    icon: 'success',
    title: title,
    text: text,
    timer: 2500,
    timerProgressBar: true,
    showConfirmButton: false,
    toast: false,
    position: 'center',
    customClass: {
      popup: 'swal-custom-popup'
    }
  });
};

const showErrorAlert = (title, text = '') => {
  Swal.fire({
    icon: 'error',
    title: title,
    text: text,
    confirmButtonColor: '#dc3545',
    confirmButtonText: 'OK',
    customClass: {
      popup: 'swal-custom-popup'
    }
  });
};

const showWarningAlert = (title, text = '') => {
  Swal.fire({
    icon: 'warning',
    title: title,
    text: text,
    confirmButtonColor: '#ffc107',
    confirmButtonText: 'OK',
    customClass: {
      popup: 'swal-custom-popup'
    }
  });
};

const showInfoAlert = (title, text = '') => {
  Swal.fire({
    icon: 'info',
    title: title,
    text: text,
    confirmButtonColor: '#004aad',
    confirmButtonText: 'OK',
    customClass: {
      popup: 'swal-custom-popup'
    }
  });
};

const showConfirmAlert = async (title, text, confirmText = 'Yes', cancelText = 'Cancel') => {
  const result = await Swal.fire({
    icon: 'question',
    title: title,
    text: text,
    showCancelButton: true,
    confirmButtonColor: '#28a745',
    cancelButtonColor: '#6c757d',
    confirmButtonText: confirmText,
    cancelButtonText: cancelText,
    customClass: {
      popup: 'swal-custom-popup'
    }
  });
  return result.isConfirmed;
};

const showDeleteConfirmAlert = async (title, text) => {
  const result = await Swal.fire({
    icon: 'warning',
    title: title,
    text: text,
    showCancelButton: true,
    confirmButtonColor: '#dc3545',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Yes, delete it!',
    cancelButtonText: 'Cancel',
    customClass: {
      popup: 'swal-custom-popup'
    }
  });
  return result.isConfirmed;
};

const showLoadingAlert = (title = 'Processing...', text = 'Please wait') => {
  Swal.fire({
    title: title,
    text: text,
    allowOutsideClick: false,
    allowEscapeKey: false,
    showConfirmButton: false,
    didOpen: () => {
      Swal.showLoading();
    }
  });
};

const closeAlert = () => {
  Swal.close();
};

// ============================================================
// STL Full View Modal — matches CaseTable's professional viewer
// ============================================================

// Icons moved to module level (avoid re-creating component types on every render)
const FileIcon = () => (
  <svg
    width="19"
    height="19"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
    <path d="M8 13h8M8 17h6" />
  </svg>
);

const UserIcon = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 21a8 8 0 0 0-16 0" />
    <circle cx="12" cy="8" r="4" />
  </svg>
);

const ClockIcon = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </svg>
);

const STLFullViewModal = React.memo(function STLFullViewModal({
  file,
  fileUrl,
  onClose,
  getResponsiveFontSize,
}) {
  const [isModelReady, setIsModelReady] = useState(false);
  const [progress, setProgress] = useState(0);

  // Prevent background scrolling while modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Close modal using Escape key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleEsc);

    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  // stable callbacks so STLViewer never sees new function identities
  const handleLoad = useCallback(() => setIsModelReady(true), []);
  const handleError = useCallback(() => setIsModelReady(true), []);

  // Format upload date/time
  const formatUploadTime = () => {
    const raw =
      file?.uploadedBy?.uploadedAt ||
      file?.createdAt ||
      file?.updatedAt;

    if (!raw) return "Unknown";

    try {
      const d = new Date(raw);

      if (isNaN(d.getTime())) return "Unknown";

      return d.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return "Unknown";
    }
  };

  const fileName =
    file?.originalName ||
    file?.fileName ||
    (file?.filePath ? file.filePath.split(/[\\/]/).pop() : "") ||
    "File";

  const uploadedBy =
    file?.uploadedBy?.userName ||
    file?.uploadedBy?.labName ||
    "Unknown User";

  const uploadTime = formatUploadTime();

  const responsive = (desktop, mobile) =>
    getResponsiveFontSize(desktop, mobile);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "rgba(0, 0, 0, 0.85)",
        zIndex: 9999,
        padding: "12px",
        boxSizing: "border-box",
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "min(1800px, 98vw)",
          height: "min(950px, 94vh)",
          backgroundColor: "#00a1d6",
          borderRadius: "10px",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ================= PROFESSIONAL HEADER ================= */}
        <div
          style={{
            backgroundColor: "#004aad",
            backgroundImage:
              "linear-gradient(115deg, #004aad 0%, #003b88 100%)",
            color: "#fff",
            padding: responsive("14px 22px", "10px 12px"),
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: responsive("20px", "10px"),
            flexShrink: 0,
            minHeight: responsive("76px", "54px"),
            boxSizing: "border-box",
            borderBottom: "1px solid rgba(255,255,255,0.18)",
          }}
        >
          {/* LEFT: File icon and name */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: responsive("12px", "8px"),
              minWidth: 0,
              flex: "1 1 auto",
            }}
          >
            <div
              style={{
                width: responsive("42px", "34px"),
                height: responsive("42px", "34px"),
                minWidth: responsive("42px", "34px"),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255,255,255,0.13)",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: "10px",
              }}
            >
              <FileIcon />
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                minWidth: 0,
              }}
            >
              <div
                style={{
                  fontSize: responsive("17px", "12px"),
                  fontWeight: 700,
                  lineHeight: 1.35,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  letterSpacing: "0.1px",
                }}
                title={fileName}
              >
                {fileName}
              </div>

              <div
                style={{
                  fontSize: responsive("11px", "9px"),
                  color: "rgba(255,255,255,0.72)",
                  letterSpacing: "0.7px",
                  textTransform: "uppercase",
                  fontWeight: 500,
                }}
              >
                3D Model Viewer
              </div>
            </div>
          </div>

          {/* RIGHT: Uploader, time, and close button */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: responsive("20px", "8px"),
              flexShrink: 0,
            }}
          >
            {/* Uploaded By */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: responsive("9px", "5px"),
                minWidth: 0,
                maxWidth: responsive("220px", "100px"),
              }}
              title={`Uploaded by: ${uploadedBy}`}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  color: "rgba(255,255,255,0.8)",
                }}
              >
                <UserIcon />
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    fontSize: responsive("10px", "8px"),
                    color: "rgba(255,255,255,0.68)",
                    fontWeight: 500,
                    textTransform: "uppercase",
                    letterSpacing: "0.6px",
                    whiteSpace: "nowrap",
                  }}
                >
                  Uploaded By
                </span>

                <span
                  style={{
                    fontSize: responsive("13px", "10px"),
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {uploadedBy}
                </span>
              </div>
            </div>

            {/* Vertical separator */}
            <div
              style={{
                width: "1px",
                height: responsive("34px", "26px"),
                backgroundColor: "rgba(255,255,255,0.25)",
                flexShrink: 0,
              }}
            />

            {/* Upload Date and Time */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: responsive("9px", "5px"),
                flexShrink: 0,
              }}
              title={`Uploaded: ${uploadTime}`}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  color: "rgba(255,255,255,0.8)",
                }}
              >
                <ClockIcon />
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                }}
              >
                <span
                  style={{
                    fontSize: responsive("10px", "8px"),
                    color: "rgba(255,255,255,0.68)",
                    fontWeight: 500,
                    textTransform: "uppercase",
                    letterSpacing: "0.6px",
                    whiteSpace: "nowrap",
                  }}
                >
                  Uploaded At
                </span>

                <span
                  style={{
                    fontSize: responsive("12px", "9px"),
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  {uploadTime}
                </span>
              </div>
            </div>

            {/* Close button */}
            <button
              onClick={onClose}
              aria-label="Close STL viewer"
              title="Close viewer"
              style={{
                backgroundColor: "rgba(255,255,255,0.12)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: "8px",
                width: responsive("36px", "28px"),
                height: responsive("36px", "28px"),
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: responsive("18px", "13px"),
                cursor: "pointer",
                flexShrink: 0,
                transition:
                  "background-color 0.2s ease, transform 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  "rgba(255,255,255,0.28)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor =
                  "rgba(255,255,255,0.12)";
              }}
            >
              &#10005;
            </button>
          </div>
        </div>

        {/* ================= BODY: STL VIEWER ================= */}
        <div
          style={{
            position: "relative",
            flex: 1,
            minHeight: 0,
            width: "100%",
          }}
        >
          {!isModelReady && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#00a1d6",
                zIndex: 5,
              }}
            >
              <div
                style={{
                  width: "50px",
                  height: "50px",
                  border: "4px solid rgba(255,255,255,0.3)",
                  borderTopColor: "#fff",
                  borderRadius: "50%",
                  animation: "stl-modal-spin 0.8s linear infinite",
                }}
              />

              <p
                style={{
                  color: "#fff",
                  marginTop: "15px",
                  fontSize: responsive("16px", "12px"),
                }}
              >
                Loading 3D Model...
                {progress > 0 && progress < 100
                  ? ` ${progress}%`
                  : ""}
              </p>

              <style>
                {`
                  @keyframes stl-modal-spin {
                    to {
                      transform: rotate(360deg);
                    }
                  }
                `}
              </style>
            </div>
          )}

          <div
            style={{
              width: "100%",
              height: "100%",
            }}
          >
            <STLViewer
              fileUrl={fileUrl}
              size="extra-large"
              shouldLoad={true}
              onProgress={setProgress}
              onLoad={handleLoad}
              onError={handleError}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

// ============================================================

const OutsourceCaseDetails = () => {
  // Get currency context
  const { currency, formatAmount } = useCurrency();

  // Pricing access — hides amounts / invoice links when user has no access
  const { hasPricingAccess, loading: pricingLoading } = usePricingAccess();

  const { caseNumber } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const multiFileInputRef = useRef(null);
  const replaceFileInputRef = useRef(null);

  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [caseId, setCaseId] = useState(null);
  const [canShowActionButton, setCanShowActionButton] = useState(false);
  const [isDestinationLab, setIsDestinationLab] = useState(false);
  const [isSourceLab, setIsSourceLab] = useState(false);

  // SINGLE File upload states
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // MULTIPLE File upload states
  const [uploadingMultiple, setUploadingMultiple] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [multiUploadProgress, setMultiUploadProgress] = useState(0);
  const [uploadStatuses, setUploadStatuses] = useState([]);
  const [showUploadSummary, setShowUploadSummary] = useState(false);

  // STL Viewer modal
  const [showSTLModal, setShowSTLModal] = useState(false);
  const [selectedSTLFile, setSelectedSTLFile] = useState(null);
  const [selectedSTLFileData, setSelectedSTLFileData] = useState(null);

  // Delete loading state
  const [deletingFileId, setDeletingFileId] = useState(null);

  // Image modal
  const [showImage, setShowImage] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  // Invoice state
  const [invoiceData, setInvoiceData] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [hasInvoice, setHasInvoice] = useState(false);

  // Notes states
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [notesHistory, setNotesHistory] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState(null);

  // Upload mode toggle
  const [uploadMode, setUploadMode] = useState('single');

  // ====================== VIEW TRACKING STATES ======================
  const [destinationViewInfo, setDestinationViewInfo] = useState(null);
  const [hasTrackedView, setHasTrackedView] = useState(false);

  // ====================== PROCEDURE EDITING STATES ======================
  const [showAddProcedureModal, setShowAddProcedureModal] = useState(false);
  const [showChangeHistoryModal, setShowChangeHistoryModal] = useState(false);
  const [availableProcedures, setAvailableProcedures] = useState([]);
  const [changeHistory, setChangeHistory] = useState([]);
  const [loadingAvailableProcedures, setLoadingAvailableProcedures] = useState(false);
  const [loadingChangeHistory, setLoadingChangeHistory] = useState(false);
  const [selectedProcedureForAdd, setSelectedProcedureForAdd] = useState(null);
  const [addProcedureQuantity, setAddProcedureQuantity] = useState(1);
  const [addProcedureNotes, setAddProcedureNotes] = useState('');
  const [editingProcedureId, setEditingProcedureId] = useState(null);
  const [editingQuantity, setEditingQuantity] = useState(1);
  const [editingNotes, setEditingNotes] = useState('');
  const [updatingQuantity, setUpdatingQuantity] = useState(false);
  const [addingProcedure, setAddingProcedure] = useState(false);

  // ====================== DELETE PROCEDURE STATE ======================
  const [deletingProcedureId, setDeletingProcedureId] = useState(null);

  // ====================== REPLACE PROCEDURE STATE ======================
  const [showReplaceProcedureModal, setShowReplaceProcedureModal] = useState(false);
  const [replacingProcedure, setReplacingProcedure] = useState(null);
  const [selectedReplacementProcedure, setSelectedReplacementProcedure] = useState(null);
  const [replacementQuantity, setReplacementQuantity] = useState(1);
  const [replacementNotes, setReplacementNotes] = useState('');
  const [replacingProcedureLoading, setReplacingProcedureLoading] = useState(false);
  const [availableReplacementProcedures, setAvailableReplacementProcedures] = useState([]);
  const [loadingReplacementProcedures, setLoadingReplacementProcedures] = useState(false);

  // ====================== UNCANCEL CASE STATE ======================
  const [uncancellingCase, setUncancellingCase] = useState(false);

  // ====================== NEW: FILE CHANGE HISTORY STATES ======================
  const [fileChangeHistory, setFileChangeHistory] = useState([]);
  const [loadingFileHistory, setLoadingFileHistory] = useState(false);
  const [showFileHistoryModal, setShowFileHistoryModal] = useState(false);

  // ====================== NEW: REPLACE FILE STATES ======================
  const [showReplaceFileModal, setShowReplaceFileModal] = useState(false);
  const [fileToReplace, setFileToReplace] = useState(null);
  const [replacementFile, setReplacementFile] = useState(null);
  const [replacementFileNotes, setReplacementFileNotes] = useState('');
  const [replacingFile, setReplacingFile] = useState(false);

  const API_BASE_URL = 'https://hms.backend.hiragroupcloud.com';

  // Responsive font helper (matches CaseTable's rf function)
  const getResponsiveFontSize = useCallback((desktopSize, mobileSize) => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 767;
    if (isMobile) return mobileSize || '10px';
    return desktopSize || '16px';
  }, []);

  useEffect(() => {
    if (caseNumber) {
      fetchOutsourceCaseDetails();
    }
  }, [caseNumber]);

  // Track view when case data is loaded
  useEffect(() => {
    if (caseData && caseData._id && !hasTrackedView) {
      trackView();
      fetchDestinationViewInfo();
      setHasTrackedView(true);
    }
  }, [caseData]);

  // Fetch invoice data when case data is loaded and has invoice
  useEffect(() => {
    if (caseData && (caseData.invoiceNumber || caseData.invoice)) {
      fetchInvoiceData();
      setHasInvoice(true);
    }
  }, [caseData]);

  // Fetch notes when case data is loaded
  useEffect(() => {
    if (caseData && caseData._id) {
      fetchNotes();
    }
  }, [caseData]);

  // Fetch file change history when case data is loaded
  useEffect(() => {
    if (caseData && caseData._id) {
      fetchFileChangeHistory();
    }
  }, [caseData]);

  // Helper function to get currency symbol
  const getCurrencySymbol = () => {
    return currency?.currencySymbol || '₮';
  };

  // ✅ Updated: returns null when the user has no pricing access.
  //    Callers must guard with `hasPricingAccess &&` before rendering.
  const formatCurrency = (amount) => {
    if (!hasPricingAccess) return null;
    if (amount === null || amount === undefined) return `${getCurrencySymbol()}0.00`;
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numericAmount)) return `${getCurrencySymbol()}0.00`;

    if (formatAmount) {
      return formatAmount(numericAmount);
    }
    return `${getCurrencySymbol()}${numericAmount.toFixed(2)}`;
  };

  // ====================== HELPER: Get user info from localStorage ======================
  const getUserInfo = () => {
    const userLabId = localStorage.getItem('userLabId') ||
                      localStorage.getItem('labId') ||
                      '';
    const userName = localStorage.getItem('userName') ||
                     localStorage.getItem('username') ||
                     'Unknown';
    const labName = localStorage.getItem('labName') ||
                    localStorage.getItem('currentLabName') ||
                    '';
    const userId = localStorage.getItem('userId') || '';
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token') || '';

    return { userLabId, userName, labName, userId, token };
  };

  // ====================== VIEW TRACKING FUNCTIONS ======================
  const trackView = async () => {
    try {
      const { token, userLabId, userName, labName } = getUserInfo();

      if (!userLabId || !caseData || !caseData._id) {
        return;
      }

      await axios.post(
        `${API_BASE_URL}/api/outsource-case/mark-seen/${caseData._id}`,
        {
          userLabId: userLabId,
          userName: userName,
          labName: labName
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'userLabId': userLabId,
            'x-user-lab-id': userLabId
          },
          withCredentials: true
        }
      );

      setTimeout(() => {
        fetchDestinationViewInfo();
      }, 500);
    } catch (error) {
      console.error('Error tracking view:', error);
    }
  };

  const fetchDestinationViewInfo = async (retryCount = 0) => {
    if (!caseData || !caseData.caseNumber) {
      return;
    }

    try {
      const { token, userLabId } = getUserInfo();

      if (!userLabId) {
        return;
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/outsource-case/view-stats/${caseData.caseNumber}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'userLabId': userLabId,
            'x-user-lab-id': userLabId
          },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        const stats = response.data.data;
        setDestinationViewInfo({
          viewedByDestination: stats.destinationLabView ? true : false,
          firstViewedAt: stats.firstViewedByDestination,
          lastViewedAt: stats.destinationLabView?.viewedAt || stats.lastViewedAt,
          isDestinationLab: stats.isDestinationLab || false,
          isSourceLab: stats.isSourceLab || false
        });
      }
    } catch (error) {
      console.error('Error fetching destination view info:', error);

      if (retryCount < 3) {
        setTimeout(() => {
          fetchDestinationViewInfo(retryCount + 1);
        }, 1000 * (retryCount + 1));
      }
    }
  };

  // Helper function to format "time ago"
  const formatTimeAgo = (dateString) => {
    if (!dateString) return null;

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // ====================== NEW: FILE CHANGE HISTORY FUNCTIONS ======================

  const fetchFileChangeHistory = async () => {
    if (!caseData || !caseData._id) return;

    setLoadingFileHistory(true);
    try {
      const { token } = getUserInfo();

      const response = await axios.get(
        `${API_BASE_URL}/api/outsource-case/${caseData._id}/file-history`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );

      if (response.data && response.data.success) {
        setFileChangeHistory(response.data.data.changeHistory || []);
      }
    } catch (error) {
      console.error('Error fetching file change history:', error);
    } finally {
      setLoadingFileHistory(false);
    }
  };

  const openFileHistoryModal = () => {
    fetchFileChangeHistory();
    setShowFileHistoryModal(true);
  };

  // ====================== NEW: REPLACE FILE FUNCTIONS ======================

  const openReplaceFileModal = (file) => {
    setFileToReplace(file);
    setReplacementFile(null);
    setReplacementFileNotes('');
    if (replaceFileInputRef.current) replaceFileInputRef.current.value = '';
    setShowReplaceFileModal(true);
  };

  const handleReplacementFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setReplacementFile(file);
    }
  };

  const handleReplaceFile = async () => {
    if (!replacementFile || !fileToReplace) {
      showWarningAlert('No File Selected', 'Please select a replacement file');
      return;
    }

    setReplacingFile(true);

    const formData = new FormData();
    formData.append('file', replacementFile);
    if (replacementFileNotes) {
      formData.append('notes', replacementFileNotes);
    }

    try {
      const { token, userLabId, userId, userName, labName } = getUserInfo();

      const response = await axios.put(
        `${API_BASE_URL}/api/outsource-case/${caseData._id}/files/${fileToReplace.fileId}/replace`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
            'userLabId': userLabId,
            'userId': userId,
            'x-user-lab-id': userLabId,
            'x-user-id': userId,
            'x-user-data': JSON.stringify({
              userLabId: userLabId,
              userId: userId,
              userName: userName,
              labName: labName
            })
          },
          withCredentials: true
        }
      );

      if (response.data.success) {
        showSuccessAlert('File Replaced!', 'File replaced successfully! Change recorded in history.');
        setShowReplaceFileModal(false);
        setFileToReplace(null);
        setReplacementFile(null);
        setReplacementFileNotes('');
        fetchOutsourceCaseDetails();
        fetchFileChangeHistory();
      }
    } catch (error) {
      console.error('Replace file error:', error);
      showErrorAlert('Replace Failed', error.response?.data?.message || 'Failed to replace file');
    } finally {
      setReplacingFile(false);
    }
  };

  // ====================== CHECK IF FILE CHANGES ARE ALLOWED ======================
  const canModifyFiles = () => {
    return caseData &&
           caseData.status !== 'completed' &&
           caseData.status !== 'cancelled';
  };

  // ====================== PROCEDURE EDITING API FUNCTIONS ======================

  const fetchAvailableProcedures = async () => {
    if (!caseData || !caseData._id) return;

    setLoadingAvailableProcedures(true);
    try {
      const { token, userLabId } = getUserInfo();

      const response = await axios.get(
        `${API_BASE_URL}/api/outsource-case/${caseData._id}/available-procedures`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'userLabId': userLabId,
            'x-user-lab-id': userLabId
          },
          withCredentials: true
        }
      );

      if (response.data && response.data.success) {
        setAvailableProcedures(response.data.data.availableProcedures || []);
      }
    } catch (error) {
      console.error('Error fetching available procedures:', error);
      showErrorAlert('Fetch Failed', error.response?.data?.message || 'Failed to fetch available procedures');
    } finally {
      setLoadingAvailableProcedures(false);
    }
  };

  const fetchChangeHistory = async () => {
    if (!caseData || !caseData._id) return;

    setLoadingChangeHistory(true);
    try {
      const { token, userLabId } = getUserInfo();

      const response = await axios.get(
        `${API_BASE_URL}/api/outsource-case/${caseData._id}/change-history`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'userLabId': userLabId,
            'x-user-lab-id': userLabId
          },
          withCredentials: true
        }
      );

      if (response.data && response.data.success) {
        setChangeHistory(response.data.data.changeHistory || []);
      }
    } catch (error) {
      console.error('Error fetching change history:', error);
      showErrorAlert('Fetch Failed', error.response?.data?.message || 'Failed to fetch change history');
    } finally {
      setLoadingChangeHistory(false);
    }
  };

  const handleAddProcedure = async () => {
    if (!selectedProcedureForAdd) {
      showWarningAlert('No Procedure Selected', 'Please select a procedure');
      return;
    }

    if (addProcedureQuantity < 1) {
      showWarningAlert('Invalid Quantity', 'Quantity must be at least 1');
      return;
    }

    setAddingProcedure(true);
    try {
      const { token, userLabId, userName, labName } = getUserInfo();

      const response = await axios.post(
        `${API_BASE_URL}/api/outsource-case/${caseData._id}/add-procedure`,
        {
          procedureId: selectedProcedureForAdd.procedureId,
          quantity: addProcedureQuantity,
          notes: addProcedureNotes
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'userLabId': userLabId,
            'x-user-lab-id': userLabId,
            'x-user-data': JSON.stringify({
              userLabId: userLabId,
              userName: userName,
              labName: labName
            })
          },
          withCredentials: true
        }
      );

      if (response.data && response.data.success) {
        showSuccessAlert('Procedure Added!', 'Procedure added successfully!');
        setShowAddProcedureModal(false);
        setSelectedProcedureForAdd(null);
        setAddProcedureQuantity(1);
        setAddProcedureNotes('');
        fetchOutsourceCaseDetails();
      }
    } catch (error) {
      console.error('Error adding procedure:', error);
      showErrorAlert('Add Failed', error.response?.data?.message || 'Failed to add procedure');
    } finally {
      setAddingProcedure(false);
    }
  };

  const handleUpdateQuantity = async (procedureId) => {
    if (editingQuantity < 1) {
      showWarningAlert('Invalid Quantity', 'Quantity must be at least 1');
      return;
    }

    setUpdatingQuantity(true);
    try {
      const { token, userLabId, userName, labName } = getUserInfo();

      const response = await axios.put(
        `${API_BASE_URL}/api/outsource-case/${caseData._id}/procedures/${procedureId}/quantity`,
        {
          quantity: editingQuantity,
          notes: editingNotes
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'userLabId': userLabId,
            'x-user-lab-id': userLabId,
            'x-user-data': JSON.stringify({
              userLabId: userLabId,
              userName: userName,
              labName: labName
            })
          },
          withCredentials: true
        }
      );

      if (response.data && response.data.success) {
        showSuccessAlert('Quantity Updated!', 'Quantity updated successfully!');
        setEditingProcedureId(null);
        setEditingQuantity(1);
        setEditingNotes('');
        fetchOutsourceCaseDetails();
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
      showErrorAlert('Update Failed', error.response?.data?.message || 'Failed to update quantity');
    } finally {
      setUpdatingQuantity(false);
    }
  };

  // ====================== DELETE PROCEDURE HANDLER ======================
  const handleDeleteProcedure = async (procedureId, procedureName) => {
    const confirmed = await showDeleteConfirmAlert(
      'Delete Procedure?',
      `Are you sure you want to delete "${procedureName || 'this procedure'}" from this case?`
    );

    if (!confirmed) {
      return;
    }

    setDeletingProcedureId(procedureId);
    try {
      const { token, userLabId, userName, labName } = getUserInfo();

      const response = await axios.delete(
        `${API_BASE_URL}/api/outsource-case/${caseData._id}/procedures/${procedureId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'userLabId': userLabId,
            'x-user-lab-id': userLabId,
            'x-user-data': JSON.stringify({
              userLabId: userLabId,
              userName: userName,
              labName: labName
            })
          },
          withCredentials: true
        }
      );

      if (response.data && response.data.success) {
        showSuccessAlert('Procedure Deleted!', 'Procedure deleted successfully!');
        if (editingProcedureId === procedureId) {
          setEditingProcedureId(null);
          setEditingQuantity(1);
          setEditingNotes('');
        }
        fetchOutsourceCaseDetails();
      }
    } catch (error) {
      console.error('Error deleting procedure:', error);
      showErrorAlert('Delete Failed', error.response?.data?.message || 'Failed to delete procedure');
    } finally {
      setDeletingProcedureId(null);
    }
  };

  // ====================== REPLACE PROCEDURE HANDLERS ======================

  const fetchAvailableReplacementProcedures = async (currentProcedureId) => {
    if (!caseData || !caseData._id) return;

    setLoadingReplacementProcedures(true);
    try {
      const { token, userLabId } = getUserInfo();

      const response = await axios.get(
        `${API_BASE_URL}/api/outsource-case/${caseData._id}/available-procedures`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'userLabId': userLabId,
            'x-user-lab-id': userLabId
          },
          withCredentials: true
        }
      );

      if (response.data && response.data.success) {
        const existingProcedureIds = (caseData.selectedProcedures || []).map(p => p.procedureId);
        const filteredProcedures = (response.data.data.availableProcedures || []).filter(
          proc => proc.procedureId !== currentProcedureId && !existingProcedureIds.includes(proc.procedureId)
        );
        setAvailableReplacementProcedures(filteredProcedures);
      }
    } catch (error) {
      console.error('Error fetching available replacement procedures:', error);
      showErrorAlert('Fetch Failed', error.response?.data?.message || 'Failed to fetch available procedures');
    } finally {
      setLoadingReplacementProcedures(false);
    }
  };

  const openReplaceProcedureModal = (procedure) => {
    setReplacingProcedure(procedure);
    setSelectedReplacementProcedure(null);
    setReplacementQuantity(procedure.quantity || 1);
    setReplacementNotes('');
    fetchAvailableReplacementProcedures(procedure.procedureId);
    setShowReplaceProcedureModal(true);
  };

  const handleReplaceProcedure = async () => {
    if (!selectedReplacementProcedure) {
      showWarningAlert('No Procedure Selected', 'Please select a replacement procedure');
      return;
    }

    if (replacementQuantity < 1) {
      showWarningAlert('Invalid Quantity', 'Quantity must be at least 1');
      return;
    }

    setReplacingProcedureLoading(true);
    try {
      const { token, userLabId, userName, labName } = getUserInfo();

      const response = await axios.put(
        `${API_BASE_URL}/api/outsource-case/${caseData._id}/procedures/${replacingProcedure.procedureId}/replace`,
        {
          newProcedureId: selectedReplacementProcedure.procedureId,
          newQuantity: replacementQuantity,
          notes: replacementNotes
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'userLabId': userLabId,
            'x-user-lab-id': userLabId,
            'x-user-data': JSON.stringify({
              userLabId: userLabId,
              userName: userName,
              labName: labName
            })
          },
          withCredentials: true
        }
      );

      if (response.data && response.data.success) {
        showSuccessAlert('Procedure Replaced!', 'Procedure replaced successfully!');
        setShowReplaceProcedureModal(false);
        setReplacingProcedure(null);
        setSelectedReplacementProcedure(null);
        setReplacementQuantity(1);
        setReplacementNotes('');
        fetchOutsourceCaseDetails();
      }
    } catch (error) {
      console.error('Error replacing procedure:', error);
      showErrorAlert('Replace Failed', error.response?.data?.message || 'Failed to replace procedure');
    } finally {
      setReplacingProcedureLoading(false);
    }
  };

  const openAddProcedureModal = () => {
    fetchAvailableProcedures();
    setSelectedProcedureForAdd(null);
    setAddProcedureQuantity(1);
    setAddProcedureNotes('');
    setShowAddProcedureModal(true);
  };

  const openChangeHistoryModal = () => {
    fetchChangeHistory();
    setShowChangeHistoryModal(true);
  };

  // ====================== MAIN DATA FETCHING ======================

  const fetchOutsourceCaseDetails = async () => {
    setLoading(true);
    setError(null);

    try {
      const { token } = getUserInfo();

      const response = await axios.get(
        `${API_BASE_URL}/api/outsource-case/case-number/${caseNumber}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );

      if (response.data && response.data.success) {
        const fetchedCaseData = response.data.data;
        setCaseData(fetchedCaseData);

        if (fetchedCaseData.originalCaseId) {
          setCaseId(caseNumber);
        } else if (fetchedCaseData.caseId) {
          setCaseId(caseNumber);
        }

        checkIfDestinationLab(fetchedCaseData);
        checkIfSourceLab(fetchedCaseData);

        if (fetchedCaseData.invoiceNumber || fetchedCaseData.invoice) {
          setHasInvoice(true);
        }
      } else {
        setError('Failed to fetch outsource case details');
      }
    } catch (error) {
      console.error('Error fetching outsource case:', error);
      setError(error.response?.data?.message || 'Failed to fetch outsource case details');
      showErrorAlert('Load Failed', 'Failed to load case details');
    } finally {
      setLoading(false);
    }
  };

  // Fetch invoice data
  const fetchInvoiceData = async () => {
    setLoadingInvoice(true);
    try {
      const { token } = getUserInfo();
      const invoiceNumber = caseData.invoiceNumber;

      if (!invoiceNumber) {
        setLoadingInvoice(false);
        return;
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/outsource-invoice/by-number/${invoiceNumber}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );

      if (response.data && response.data.success) {
        setInvoiceData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching invoice:', error);
      setInvoiceData(null);
    } finally {
      setLoadingInvoice(false);
    }
  };

  // ====================== NOTES API FUNCTIONS ======================

  const fetchNotes = async () => {
    if (!caseData || !caseData._id) return;

    setLoadingNotes(true);
    try {
      const { token, userLabId, userId } = getUserInfo();

      const response = await axios.get(
        `${API_BASE_URL}/api/outsource-case/${caseData._id}/notes`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'userLabId': userLabId,
            'x-user-lab-id': userLabId,
            'x-user-id': userId
          },
          withCredentials: true
        }
      );

      if (response.data && response.data.success) {
        let apiNotes = response.data.data.notesHistory || [];

        let initialNotes = [];

        if (caseData.notes && typeof caseData.notes === 'string' && caseData.notes.trim()) {
          initialNotes.push({
            noteId: `initial-note-${Date.now()}`,
            _id: `initial-${Date.now()}`,
            content: caseData.notes,
            createdAt: caseData.createdAt || new Date().toISOString(),
            addedBy: {
              labName: caseData.sourceLab?.labName || 'Source Lab',
              labRole: 'source',
              userName: caseData.createdBy?.name || 'System',
              userLabId: caseData.sourceLab?.userLabId
            }
          });
        }

        if (caseData.notesHistory && Array.isArray(caseData.notesHistory) && caseData.notesHistory.length > 0) {
          const formattedInitialNotes = caseData.notesHistory.map(note => ({
            ...note,
            noteId: note.noteId || note._id || `initial-${Date.now()}-${Math.random()}`,
            _id: note._id || `initial-${Date.now()}-${Math.random()}`,
            addedBy: note.addedBy || {
              labName: caseData.sourceLab?.labName || 'Source Lab',
              labRole: 'source',
              userName: caseData.createdBy?.name || 'System'
            }
          }));
          initialNotes = [...initialNotes, ...formattedInitialNotes];
        }

        const allNotes = [...initialNotes, ...apiNotes];

        const uniqueNotes = [];
        const seen = new Set();

        for (const note of allNotes) {
          const key = `${note.content}-${new Date(note.createdAt).toISOString()}`;
          if (!seen.has(key)) {
            seen.add(key);
            uniqueNotes.push(note);
          }
        }

        uniqueNotes.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        setNotesHistory(uniqueNotes);
      } else {
        let initialNotes = [];
        if (caseData.notes && typeof caseData.notes === 'string' && caseData.notes.trim()) {
          initialNotes.push({
            noteId: `initial-note-${Date.now()}`,
            _id: `initial-${Date.now()}`,
            content: caseData.notes,
            createdAt: caseData.createdAt || new Date().toISOString(),
            addedBy: {
              labName: caseData.sourceLab?.labName || 'Source Lab',
              labRole: 'source',
              userName: caseData.createdBy?.name || 'System',
              userLabId: caseData.sourceLab?.userLabId
            }
          });
        }

        if (caseData.notesHistory && Array.isArray(caseData.notesHistory) && caseData.notesHistory.length > 0) {
          const formattedInitialNotes = caseData.notesHistory.map(note => ({
            ...note,
            addedBy: note.addedBy || {
              labName: caseData.sourceLab?.labName || 'Source Lab',
              labRole: 'source'
            }
          }));
          initialNotes = [...initialNotes, ...formattedInitialNotes];
        }

        setNotesHistory(initialNotes);
      }
    } catch (error) {
      console.error('Error fetching notes:', error);
      let initialNotes = [];
      if (caseData.notes && typeof caseData.notes === 'string' && caseData.notes.trim()) {
        initialNotes.push({
          noteId: `initial-note-${Date.now()}`,
          _id: `initial-${Date.now()}`,
          content: caseData.notes,
          createdAt: caseData.createdAt || new Date().toISOString(),
          addedBy: {
            labName: caseData.sourceLab?.labName || 'Source Lab',
            labRole: 'source',
            userName: caseData.createdBy?.name || 'System'
          }
        });
      }
      setNotesHistory(initialNotes);
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      showWarningAlert('Empty Note', 'Please enter a note');
      return;
    }

    setAddingNote(true);
    try {
      const { token, userLabId, userName, labName, userId } = getUserInfo();

      const response = await axios.post(
        `${API_BASE_URL}/api/outsource-case/${caseData._id}/notes`,
        { content: newNote.trim() },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'userLabId': userLabId,
            'userId': userId,
            'x-user-lab-id': userLabId,
            'x-user-id': userId,
            'x-user-data': JSON.stringify({
              userLabId: userLabId,
              userId: userId,
              userName: userName,
              labName: labName
            })
          },
          withCredentials: true
        }
      );

      if (response.data && response.data.success) {
        showSuccessAlert('Note Added!', 'Note added successfully!');
        setNewNote('');
        setShowAddNoteModal(false);
        fetchNotes();
      }
    } catch (error) {
      console.error('Error adding note:', error);
      showErrorAlert('Add Failed', error.response?.data?.message || 'Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (typeof noteId === 'string' && noteId.startsWith('initial-')) {
      showWarningAlert('Cannot Delete', 'Cannot delete initial notes from source lab');
      return;
    }

    const confirmed = await showDeleteConfirmAlert(
      'Delete Note?',
      'Are you sure you want to delete this note?'
    );

    if (!confirmed) {
      return;
    }

    setDeletingNoteId(noteId);
    try {
      const { token, userLabId, userId } = getUserInfo();

      const response = await axios.delete(
        `${API_BASE_URL}/api/outsource-case/${caseData._id}/notes/${noteId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'userLabId': userLabId,
            'userId': userId,
            'x-user-lab-id': userLabId,
            'x-user-id': userId
          },
          withCredentials: true
        }
      );

      if (response.data && response.data.success) {
        showSuccessAlert('Note Deleted!', 'Note deleted successfully!');
        fetchNotes();
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      showErrorAlert('Delete Failed', error.response?.data?.message || 'Failed to delete note');
    } finally {
      setDeletingNoteId(null);
    }
  };

  const checkIfDestinationLab = (caseData) => {
    const storedUserLabId = String(localStorage.getItem('userLabId') || '').trim();
    const destinationLabId = String(caseData.destinationLab?.userLabId || '').trim();

    if (storedUserLabId && destinationLabId) {
      const isDest = storedUserLabId.toLowerCase() === destinationLabId.toLowerCase();
      setIsDestinationLab(isDest);
      setCanShowActionButton(isDest);
    } else {
      setIsDestinationLab(false);
      setCanShowActionButton(false);
    }
  };

  const checkIfSourceLab = (caseData) => {
    const storedUserLabId = String(localStorage.getItem('userLabId') || '').trim();
    const sourceLabId = String(caseData.sourceLab?.userLabId || '').trim();

    if (storedUserLabId && sourceLabId) {
      const isSrc = storedUserLabId.toLowerCase() === sourceLabId.toLowerCase();
      setIsSourceLab(isSrc);
    } else {
      setIsSourceLab(false);
    }

    console.log('🔎 [SourceLab Check]', {
      storedUserLabId,
      sourceLabId,
      caseStatus: caseData.status,
      matched: storedUserLabId && sourceLabId
        ? storedUserLabId.toLowerCase() === sourceLabId.toLowerCase()
        : false
    });
  };

  const handleBackNavigation = () => {
    if (isDestinationLab) {
      navigate('/outsource-received');
    } else if (isSourceLab) {
      navigate('/toOutsource');
    } else {
      navigate('/toOutsource');
    }
  };

  const handleCaseUpdate = (updatedCase) => {
    fetchOutsourceCaseDetails();
  };

  const getFileUrl = (filePath) => {
    if (!filePath) return '';
    const filename = filePath.split(/[\\/]/).pop();
    return `${API_BASE_URL}/uploads/${filePath}`;
  };

  const getFileCategory = (file) => {
    const fileType = file.fileType || '';
    if (fileType === 'stl') return 'stl';
    if (fileType === 'image') return 'image';
    if (file.filePath && /\.(png|jpg|jpeg)$/i.test(file.filePath)) return 'image';
    return 'other';
  };

  // ✅ No navigation when no pricing access — invoice is visible-only.
  const handleViewInvoice = () => {
    if (!hasPricingAccess) return;
    const invoiceNumber = caseData.invoiceNumber || invoiceData?.invoiceNumber;
    if (invoiceNumber) {
      navigate(`/outsource-invoice/${invoiceNumber}`);
    }
  };

  // ====================== SINGLE FILE UPLOAD HANDLERS ======================
  const handleSingleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUploadSingleFile = async () => {
    if (!selectedFile || !caseData) {
      showWarningAlert('No File Selected', 'Please select a file to upload');
      return;
    }

    if (!canModifyFiles()) {
      showErrorAlert(
        'Upload Not Allowed',
        caseData.status === 'completed'
          ? 'Cannot upload files to a completed case. Files are locked for record keeping.'
          : 'Cannot upload files to a cancelled case.'
      );
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const { token, userLabId, userId, userName, labName } = getUserInfo();

      const response = await axios.post(
        `${API_BASE_URL}/api/outsource-case/${caseData._id}/upload-file`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
            'userLabId': userLabId,
            'userId': userId,
            'x-user-lab-id': userLabId,
            'x-user-id': userId,
            'x-user-data': JSON.stringify({
              userLabId: userLabId,
              userId: userId,
              userName: userName,
              labName: labName
            })
          },
          withCredentials: true,
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percent);
          }
        }
      );

      if (response.data.success) {
        showSuccessAlert('File Uploaded!', 'File uploaded successfully! Change recorded in history.');
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        fetchOutsourceCaseDetails();
        fetchFileChangeHistory();
      }
    } catch (error) {
      console.error('Upload error:', error);
      showErrorAlert('Upload Failed', error.response?.data?.message || 'Failed to upload file');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // ====================== MULTIPLE FILE UPLOAD HANDLERS ======================
  const handleMultipleFileSelect = (e) => {
    const files = Array.from(e.target.files);

    if (files.length > 10) {
      showWarningAlert('Too Many Files', 'Maximum 10 files allowed at once');
      return;
    }

    const invalidFiles = files.filter(file => {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      const allowedExtensions = [
        '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.tiff', '.tif',
        '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.txt', '.rtf',
        '.zip', '.rar', '.7z', '.tar', '.gz',
        '.stl', '.obj', '.ply', '.3mf', '.sla',
        '.constructioninfo', '.json', '.xml', '.dxf', '.dwg', '.step', '.stp',
        '.iges', '.igs'
      ];
      return !allowedExtensions.includes(ext);
    });

    if (invalidFiles.length > 0) {
      showErrorAlert(
        'Invalid File Type',
        `Invalid file type(s): ${invalidFiles.map(f => f.name).join(', ')}`
      );
      return;
    }

    const initialStatuses = files.map(file => ({
      name: file.name,
      size: file.size,
      status: 'pending',
      progress: 0,
      error: null
    }));

    setSelectedFiles(files);
    setUploadStatuses(initialStatuses);
  };

  const handleUploadMultipleFiles = async () => {
    if (!selectedFiles || selectedFiles.length === 0 || !caseData) {
      showWarningAlert('No Files Selected', 'Please select files to upload');
      return;
    }

    if (!canModifyFiles()) {
      showErrorAlert(
        'Upload Not Allowed',
        caseData.status === 'completed'
          ? 'Cannot upload files to a completed case. Files are locked for record keeping.'
          : 'Cannot upload files to a cancelled case.'
      );
      return;
    }

    setUploadingMultiple(true);
    setShowUploadSummary(true);

    const { token, userLabId, userId, userName, labName } = getUserInfo();
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];

      setUploadStatuses(prev => {
        const updated = [...prev];
        updated[i] = { ...updated[i], status: 'uploading' };
        return updated;
      });

      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await axios.post(
          `${API_BASE_URL}/api/outsource-case/${caseData._id}/upload-file`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data',
              'userLabId': userLabId,
              'userId': userId,
              'x-user-lab-id': userLabId,
              'x-user-id': userId,
              'x-user-data': JSON.stringify({
                userLabId: userLabId,
                userId: userId,
                userName: userName,
                labName: labName
              })
            },
            withCredentials: true,
            onUploadProgress: (progressEvent) => {
              const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadStatuses(prev => {
                const updated = [...prev];
                updated[i] = { ...updated[i], progress: percent };
                return updated;
              });
            }
          }
        );

        if (response.data.success) {
          successCount++;
          setUploadStatuses(prev => {
            const updated = [...prev];
            updated[i] = { ...updated[i], status: 'success', progress: 100 };
            return updated;
          });
        } else {
          errorCount++;
          setUploadStatuses(prev => {
            const updated = [...prev];
            updated[i] = {
              ...updated[i],
              status: 'error',
              error: response.data.message || 'Upload failed'
            };
            return updated;
          });
        }
      } catch (error) {
        errorCount++;
        setUploadStatuses(prev => {
          const updated = [...prev];
          updated[i] = {
            ...updated[i],
            status: 'error',
            error: error.response?.data?.message || error.message || 'Upload failed'
          };
          return updated;
        });
      }

      setMultiUploadProgress(Math.round(((i + 1) / selectedFiles.length) * 100));
    }

    setUploadingMultiple(false);

    if (successCount > 0) {
      showSuccessAlert(
        'Files Uploaded!',
        `${successCount} file(s) uploaded successfully!${errorCount > 0 ? ` ${errorCount} failed.` : ''}`
      );
      fetchOutsourceCaseDetails();
      fetchFileChangeHistory();
    } else if (errorCount > 0) {
      showErrorAlert('Upload Failed', 'All file uploads failed. Please try again.');
    }

    setTimeout(() => {
      setSelectedFiles([]);
      setUploadStatuses([]);
      setMultiUploadProgress(0);
      if (multiFileInputRef.current) multiFileInputRef.current.value = '';
    }, 2000);
  };

  const clearSelectedFiles = () => {
    setSelectedFiles([]);
    setUploadStatuses([]);
    setMultiUploadProgress(0);
    setShowUploadSummary(false);
    if (multiFileInputRef.current) multiFileInputRef.current.value = '';
  };

  // ====================== FILE DOWNLOAD ======================
  // ✅ Success alert removed — download happens silently.
  //    Error alert is preserved for failure cases.
  const handleFileDownload = async (file) => {
    try {
      const fileUrl = getFileUrl(file.filePath);
      const response = await fetch(fileUrl, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = file.originalName || file.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      // ✅ No success alert on download — kept silent for smoother UX
    } catch (error) {
      console.error('File download failed:', error);
      showErrorAlert('Download Failed', 'Failed to download file');
    }
  };

  // ====================== UPDATED: handleDeleteFile with completion check ======================
  const handleDeleteFile = async (fileId) => {
    if (!canModifyFiles()) {
      showErrorAlert(
        'Delete Not Allowed',
        caseData.status === 'completed'
          ? 'Cannot delete files from a completed case. Files are locked for record keeping.'
          : 'Cannot delete files from a cancelled case.'
      );
      return;
    }

    const confirmed = await showDeleteConfirmAlert(
      'Delete File?',
      'Are you sure you want to delete this file? This action will be recorded in the case history.'
    );

    if (!confirmed) {
      return;
    }

    setDeletingFileId(fileId);

    try {
      const { token, userLabId, userId } = getUserInfo();

      const response = await axios.delete(
        `${API_BASE_URL}/api/outsource-case/${caseData._id}/files/${fileId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'userLabId': userLabId,
            'userId': userId,
            'x-user-lab-id': userLabId,
            'x-user-id': userId
          },
          withCredentials: true
        }
      );

      if (response.data.success) {
        showSuccessAlert('File Deleted!', 'File deleted successfully. Change recorded in history.');
        fetchOutsourceCaseDetails();
        fetchFileChangeHistory();
      }
    } catch (error) {
      console.error('Delete error:', error);
      showErrorAlert('Delete Failed', error.response?.data?.message || 'Failed to delete file');
    } finally {
      setDeletingFileId(null);
    }
  };

  // Updated: store file data for the professional modal
  const handleViewSTL = (file) => {
    const fileUrl = getFileUrl(file.filePath);
    setSelectedSTLFile(fileUrl);
    setSelectedSTLFileData(file);
    setShowSTLModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return 'Unknown size';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return '#856404';
      case 'accepted': return '#084298';
      case 'in_progress': return '#0d6efd';
      case 'completed': return '#0f5132';
      case 'cancelled': return '#721c24';
      default: return '#6c757d';
    }
  };

  const getStatusBgColor = (status) => {
    switch(status) {
      case 'pending': return '#fff3cd';
      case 'accepted': return '#cfe2ff';
      case 'in_progress': return '#cfe2ff';
      case 'completed': return '#d1e7dd';
      case 'cancelled': return '#f8d7da';
      default: return '#e9ecef';
    }
  };

  const handleInvoiceSuccess = () => {
    setHasInvoice(true);
    fetchOutsourceCaseDetails();
  };

  // ==================== FILE CARD RENDERERS ====================

  const renderSTLFileCard = (file, index, isAdditional = true) => {
    const fileUrl = getFileUrl(file.filePath);
    const canModify = canModifyFiles();

    return (
      <div key={file.fileId || index} className="outsource-file-card">
        {file.isReplaced && (
          <Badge bg="info" className="replacement-badge">
            Replaced
          </Badge>
        )}

        <STLViewer fileUrl={fileUrl} size="small" />
        <p><b>Date:</b> {formatDateTime(file.uploadedBy?.uploadedAt || file.createdAt)}</p>
        <p><b>File:</b> {file.originalName || file.fileName}</p>
        {file.uploadedBy && <p><b>From:</b> {file.uploadedBy.userName || 'Lab'}</p>}

        {file.isReplaced && file.replacedBy && (
          <p className="replaced-info">Replaced by: {file.replacedBy.userName}</p>
        )}

        <div className="outsource-file-actions">
          <button onClick={() => handleViewSTL(file)} className="outsource-file-action-btn view" title="View 3D Model">
            <PiImage />
          </button>
          <button onClick={() => handleFileDownload(file)} className="outsource-file-action-btn download" title="Download">
            <FaDownload />
          </button>
          {isAdditional && canModify && (
            <>
              <button onClick={() => openReplaceFileModal(file)} className="outsource-file-action-btn replace" title="Replace File">
                <FaExchangeAlt />
              </button>
              <button onClick={() => handleDeleteFile(file.fileId)} disabled={deletingFileId === file.fileId} className="outsource-file-action-btn delete" title="Delete">
                {deletingFileId === file.fileId ? <FaSpinner className="spinner-border spinner-border-sm" /> : <FaTrash />}
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderImageFileCard = (file, index, isAdditional = true) => {
    const imageUrl = getFileUrl(file.filePath);
    const canModify = canModifyFiles();

    return (
      <div key={file.fileId || index} className="outsource-file-card">
        {file.isReplaced && (
          <Badge bg="info" className="replacement-badge">
            Replaced
          </Badge>
        )}

        <img
          src={imageUrl}
          alt={file.originalName || file.fileName}
          onError={(e) => { e.target.src = '/placeholder.png'; }}
          onClick={() => setShowImage(imageUrl)}
        />
        <p><b>Date:</b> {formatDateTime(file.uploadedBy?.uploadedAt || file.createdAt)}</p>
        <p><b>File:</b> {file.originalName || file.fileName}</p>
        {file.uploadedBy && <p><b>From:</b> {file.uploadedBy.userName || 'Lab'}</p>}

        {file.isReplaced && file.replacedBy && (
          <p className="replaced-info">Replaced by: {file.replacedBy.userName}</p>
        )}

        <div className="outsource-file-actions">
          <button onClick={() => setShowImage(imageUrl)} className="outsource-file-action-btn view" title="View Image">
            <PiImage />
          </button>
          <button onClick={() => handleFileDownload(file)} className="outsource-file-action-btn download" title="Download">
            <FaDownload />
          </button>
          {isAdditional && canModify && (
            <>
              <button onClick={() => openReplaceFileModal(file)} className="outsource-file-action-btn replace" title="Replace File">
                <FaExchangeAlt />
              </button>
              <button onClick={() => handleDeleteFile(file.fileId)} disabled={deletingFileId === file.fileId} className="outsource-file-action-btn delete" title="Delete">
                {deletingFileId === file.fileId ? <FaSpinner className="spinner-border spinner-border-sm" /> : <FaTrash />}
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderOtherFileCard = (file, index, isAdditional = true) => {
    const isPdf = file.fileType === 'pdf' || (file.filePath && file.filePath.toLowerCase().endsWith('.pdf'));
    const canModify = canModifyFiles();

    return (
      <div key={file.fileId || index} className="outsource-file-card">
        {file.isReplaced && (
          <Badge bg="info" className="replacement-badge">
            Replaced
          </Badge>
        )}

        {isPdf ? <FaFilePdf style={{ fontSize: '3rem', color: 'red' }} /> : <FaFile style={{ fontSize: '3rem' }} />}
        <p><b>Date:</b> {formatDateTime(file.uploadedBy?.uploadedAt || file.createdAt)}</p>
        <p><b>File:</b> {file.originalName || file.fileName}</p>
        {file.uploadedBy && <p><b>From:</b> {file.uploadedBy.userName || 'Lab'}</p>}

        {file.isReplaced && file.replacedBy && (
          <p className="replaced-info">Replaced by: {file.replacedBy.userName}</p>
        )}

        <div className="outsource-file-actions">
          <button onClick={() => handleFileDownload(file)} className="outsource-file-action-btn download" title="Download">
            <FaDownload />
          </button>
          {isAdditional && canModify && (
            <>
              <button onClick={() => openReplaceFileModal(file)} className="outsource-file-action-btn replace" title="Replace File">
                <FaExchangeAlt />
              </button>
              <button onClick={() => handleDeleteFile(file.fileId)} disabled={deletingFileId === file.fileId} className="outsource-file-action-btn delete" title="Delete">
                {deletingFileId === file.fileId ? <FaSpinner className="spinner-border spinner-border-sm" /> : <FaTrash />}
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderFileSection = (title, files, type, showUploadSection = false, isAdditional = true) => {
    if (!files || files.length === 0) return null;

    let filteredFiles = [];
    if (type === 'stl') {
      filteredFiles = files.filter(f => f.fileType === 'stl' || (f.filePath && f.filePath.toLowerCase().endsWith('.stl')));
    } else if (type === 'image') {
      filteredFiles = files.filter(f => f.fileType === 'image' || (f.filePath && /\.(png|jpg|jpeg)$/i.test(f.filePath)));
    } else {
      filteredFiles = files.filter(f => f.fileType !== 'stl' && f.fileType !== 'image' && !(f.filePath && /\.(stl|png|jpg|jpeg)$/i.test(f.filePath)));
    }

    if (filteredFiles.length === 0) return null;

    return (
      <>
        <div className="outsource-file-section-header">
          <h4>{title} ({filteredFiles.length})</h4>
        </div>
        <div className="outsource-files-grid">
          {filteredFiles.map((file, index) => {
            if (type === 'stl') return renderSTLFileCard(file, index, isAdditional);
            if (type === 'image') return renderImageFileCard(file, index, isAdditional);
            return renderOtherFileCard(file, index, isAdditional);
          })}
        </div>
      </>
    );
  };

  const handleUncancelCase = async () => {
    const confirmed = await showConfirmAlert(
      'Reactivate Case?',
      'Are you sure you want to reactivate this cancelled case?',
      'Yes, reactivate',
      'Cancel'
    );

    if (!confirmed) {
      return;
    }

    setUncancellingCase(true);
    try {
      const { token, userLabId, userName, labName } = getUserInfo();
      const cleanCaseNumber = String(caseData.caseNumber).trim();

      const response = await axios.put(
        `${API_BASE_URL}/api/outsource-case/uncancel/${cleanCaseNumber}`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'userLabId': userLabId,
            'x-user-lab-id': userLabId,
            'x-user-data': JSON.stringify({ userLabId, userName, labName })
          },
          withCredentials: true
        }
      );

      if (response.data && response.data.success) {
        showSuccessAlert('Case Reactivated!', 'Case reactivated successfully!');
        fetchOutsourceCaseDetails();
      }
    } catch (error) {
      console.error('Error uncancelling case:', error);
      showErrorAlert('Reactivation Failed', error.response?.data?.message || 'Failed to reactivate case');
    } finally {
      setUncancellingCase(false);
    }
  };

  if (loading || pricingLoading) {
    return (
      <>
        <Header />
        <Navvbar />
        <div className="outsource-loading-container">
          <Spinner animation="border" className="outsource-spinner" />
          <p>Loading case details...</p>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !caseData) {
    return (
      <>
        <Header />
        <Navvbar />
        <div className="outsource-error-container">
          <p>Error: {error || 'Case not found'}</p>
          <Button variant="outline-danger" onClick={() => navigate('/toOutsource')}>
            Go Back
          </Button>
        </div>
        <Footer />
      </>
    );
  }

  const procedures = caseData.selectedProcedures || [];
  let procedureList = procedures;
  if (procedureList.length === 0 && caseData.selectedProcedure) {
    procedureList = [caseData.selectedProcedure];
  }

  const totalQuantity = procedureList.reduce((sum, proc) => sum + (proc.quantity || 0), 0);
  const totalPrice = caseData.pricingSummary?.totalPrice ||
                     procedureList.reduce((sum, proc) => sum + (proc.totalPrice || 0), 0);
  const subtotal = caseData.pricingSummary?.subtotal || totalPrice;

  const originalFiles = caseData.selectedFiles || [];
  const additionalFiles = caseData.uploadedFiles || [];

  const showEditButtons = isSourceLab && caseData.status !== 'completed' && caseData.status !== 'cancelled';

  return (
    <>
      <Header />
      <Navvbar />

      <div className="outsource-case-container">
        {/* Back Button */}
        <div className="outsource-header-actions">
          <button onClick={handleBackNavigation} className="outsource-back-btn">
            <FaArrowLeft /> {isDestinationLab ? 'Back to Outsource Received' : 'Back to Outsource Cases'}
          </button>

          <div className="outsource-actions-group">
            {/* ✅ Create Invoice button is hidden when no pricing access */}
            {isDestinationLab && !hasInvoice && hasPricingAccess && (
              <button onClick={() => setShowInvoiceModal(true)} className="outsource-create-invoice-btn">
                <FaFileInvoice /> Create Invoice
              </button>
            )}

            {caseId && isDestinationLab && (
              <OutsourceActionButton
                caseId={caseId}
                caseNumber={caseNumber}
                onStatusUpdate={handleCaseUpdate}
                currentCase={caseData}
              />
            )}
          </div>
        </div>

        {/* Main Card Container */}
        <div className="outsource-main-card">
          {/* Header */}
          <div className="outsource-card-header">
            <h2>Outsource Case Details</h2>
            <p>
              Case #{caseData.caseNumber}
              {/* ✅ Re-outsourced case: also show the original case number */}
              {caseData.isReOutsource && caseData.originalCaseNumber && (
                <span style={{ marginLeft: '12px', fontSize: '13px', opacity: 0.85 }}>
                  (Original Case #{caseData.originalCaseNumber})
                </span>
              )}
            </p>
          </div>

          {/* Content */}
          <div className="outsource-card-content">
            {/* Status Banner */}
            <div
              className="outsource-status-banner"
              style={{
                backgroundColor: getStatusBgColor(caseData.status),
                color: getStatusColor(caseData.status)
              }}
            >
              Status: {caseData.status?.replace('_', ' ') || 'PENDING'}
            </div>

            {caseData.status === 'cancelled' && (
              <div className="outsource-cancelled-banner">
                <div className="outsource-cancelled-info">
                  <FaTimesCircle style={{ fontSize: '20px', color: '#dc3545' }} />
                  <div>
                    <strong style={{ color: '#721c24' }}>This case is cancelled</strong>
                    {caseData.cancelledBy && (
                      <p style={{ margin: '5px 0 0 0', color: '#721c24', fontSize: '13px' }}>
                        Cancelled by {caseData.cancelledBy.labName || caseData.cancelledBy.userName || 'Unknown'}
                        {caseData.cancelledAt && ` on ${formatDateTime(caseData.cancelledAt)}`}
                        {caseData.cancelledBy.reason && ` — Reason: ${caseData.cancelledBy.reason}`}
                      </p>
                    )}
                  </div>
                </div>

                {(isSourceLab || isDestinationLab) && (
                  <button onClick={handleUncancelCase} disabled={uncancellingCase} className="outsource-uncancel-btn">
                    {uncancellingCase ? (
                      <><FaSpinner className="spinner-border spinner-border-sm" /> Reactivating...</>
                    ) : (
                      <><FaUndo /> Uncancel Case</>
                    )}
                  </button>
                )}
              </div>
            )}

            {/* View Information Banner */}
            {destinationViewInfo && (
              <div className="outsource-view-banner">
                <FaEye style={{ fontSize: '18px', color: '#004aad' }} />
                {destinationViewInfo.viewedByDestination ? (
                  <>
                    <span style={{ color: '#155724', fontWeight: '500' }}>
                      <FaCheckCircle style={{ color: '#28a745', marginRight: '6px' }} />
                      Destination lab has viewed this case
                    </span>
                    <span style={{ fontSize: '14px', color: '#004aad' }}>
                      {formatTimeAgo(destinationViewInfo.firstViewedAt)}
                    </span>
                  </>
                ) : (
                  <span style={{ color: '#856404', fontWeight: '500' }}>
                    <span style={{ marginRight: '6px' }}>🕐</span>
                    Not yet viewed by destination lab
                  </span>
                )}
              </div>
            )}

            {/* Invoice Info Banner — invoice number shown always, clickable only with pricing access */}
            {hasInvoice && (caseData.invoiceNumber || invoiceData) && (
              <div className="outsource-invoice-banner">
                <FaFileInvoiceDollar style={{ fontSize: '24px', color: '#155724' }} />
                <div>
                  <strong style={{ color: '#155724' }}>Invoice Generated</strong>
                  <p style={{ margin: '5px 0 0 0', color: '#155724', fontSize: '14px' }}>
                    Invoice Number:{' '}
                    {hasPricingAccess ? (
                      <span
                        onClick={handleViewInvoice}
                        style={{ fontWeight: 'bold', color: '#004aad', cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        {caseData.invoiceNumber || invoiceData?.invoiceNumber}
                      </span>
                    ) : (
                      <span style={{ fontWeight: 'bold', color: '#155724' }}>
                        {caseData.invoiceNumber || invoiceData?.invoiceNumber}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            )}

            {/* Two Column Layout */}
            <div className="outsource-two-column">
              <div className="outsource-lab-card">
                <h4>Source Laboratory</h4>
                <p><strong>Lab Name:</strong> {caseData.sourceLab?.labName || 'N/A'}</p>
                <p><strong>Lab ID:</strong> {caseData.sourceLab?.userLabId || 'N/A'}</p>
                {isSourceLab && (
                  <p style={{ color: '#004aad', fontWeight: 'bold' }}>
                    <FaCheckCircle style={{ color: '#28a745', marginRight: '5px' }} />
                    You are the source lab
                  </p>
                )}
              </div>

              <div className="outsource-lab-card">
                <h4>Destination Laboratory</h4>
                <p><strong>Lab Name:</strong> {caseData.destinationLab?.labName || 'N/A'}</p>
                <p><strong>Lab ID:</strong> {caseData.destinationLab?.userLabId || 'N/A'}</p>
                {isDestinationLab && (
                  <p style={{ color: '#004aad', fontWeight: 'bold' }}>
                    <FaCheckCircle style={{ color: '#28a745', marginRight: '5px' }} />
                    You are the destination lab
                  </p>
                )}
              </div>
            </div>

            {/* Procedure Details */}
            <div className="outsource-procedure-section">
              <div className="outsource-procedure-header">
                <h4>Procedure Details ({procedureList.length})</h4>
                <div className="outsource-procedure-actions">
                  {showEditButtons && (
                    <Button variant="success" size="sm" onClick={openAddProcedureModal} style={{ backgroundColor: '#28a745', borderColor: '#28a745' }}>
                      <FaPlus style={{ marginRight: '5px' }} /> Add Procedure
                    </Button>
                  )}
                </div>
              </div>

              {procedureList.length === 0 ? (
                <p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>No procedures found for this case.</p>
              ) : (
                <>
                  {procedureList.map((procedure, index) => {
                    const isNewlyAdded = procedure.isAdded === true;
                    const hasBeenUpdated = procedure.updatedAt && procedure.updatedAt !== procedure.addedAt;

                    return (
                      <div key={procedure._id || procedure.procedureId || index} className={`outsource-procedure-item ${isNewlyAdded ? 'newly-added' : 'normal'}`}>
                        {isNewlyAdded && (
                          <Badge bg="success" style={{ position: 'absolute', top: '-10px', right: '10px', fontSize: '10px' }}>Newly Added</Badge>
                        )}
                        {hasBeenUpdated && (
                          <Badge bg="warning" style={{ position: 'absolute', top: '-10px', right: isNewlyAdded ? '130px' : '10px', fontSize: '10px', color: '#333' }}>Updated</Badge>
                        )}

                        <div className="outsource-procedure-title">
                          <h5>
                            <span className="outsource-procedure-number">#{index + 1}</span>
                            {procedure.procedureName || 'Unnamed Procedure'}
                          </h5>
                          <div className="outsource-procedure-buttons">
                            {/* ✅ Price badge hidden when no pricing access */}
                            {hasPricingAccess && (
                              <span className="outsource-price-badge">
                                {formatCurrency(procedure.totalPrice || (procedure.price * procedure.quantity))}
                              </span>
                            )}
                            {showEditButtons && (
                              <>
                                <button onClick={() => openReplaceProcedureModal(procedure)} className="outsource-replace-btn" title="Replace this procedure">
                                  <FaExchangeAlt /> Replace
                                </button>
                                <button onClick={() => handleDeleteProcedure(procedure.procedureId, procedure.procedureName)} disabled={deletingProcedureId === procedure.procedureId} className="outsource-delete-btn" title="Delete this procedure">
                                  {deletingProcedureId === procedure.procedureId ? <FaSpinner className="spinner-border spinner-border-sm" /> : <><FaTrash /> Delete</>}
                                </button>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="outsource-procedure-details">
                          {/* ✅ Unit price hidden when no pricing access */}
                          {hasPricingAccess && (
                            <div><strong>Unit Price:</strong> {formatCurrency(procedure.price || 0)}</div>
                          )}
                          <div>
                            <strong>Quantity:</strong>
                            {editingProcedureId === procedure.procedureId ? (
                              <div className="outsource-quantity-edit">
                                <input type="number" min="1" value={editingQuantity} onChange={(e) => setEditingQuantity(parseInt(e.target.value) || 1)} className="outsource-quantity-input" />
                                <button onClick={() => handleUpdateQuantity(procedure.procedureId)} disabled={updatingQuantity} className="outsource-save-btn">
                                  {updatingQuantity ? <FaSpinner className="spinner-border spinner-border-sm" /> : <FaSave />}
                                </button>
                                <button onClick={() => { setEditingProcedureId(null); setEditingQuantity(1); setEditingNotes(''); }} className="outsource-cancel-btn">
                                  <FaTimes />
                                </button>
                                <input type="text" value={editingNotes} onChange={(e) => setEditingNotes(e.target.value)} placeholder="Reason for change (optional)" className="outsource-notes-input" />
                              </div>
                            ) : (
                              <>
                                <span className="outsource-qty-badge">{procedure.quantity || 1}</span>
                                {showEditButtons && (
                                  <button onClick={() => { setEditingProcedureId(procedure.procedureId); setEditingQuantity(procedure.quantity || 1); setEditingNotes(''); }} className="outsource-edit-qty-btn">
                                    <FaEdit /> Edit Qty
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                          <div><strong>Pricing Type:</strong> {procedure.pricingType || 'N/A'}</div>
                          <div><strong>Shared By:</strong> {procedure.sharedByName || 'N/A'}</div>
                          {/* ✅ Total price hidden when no pricing access */}
                          {hasPricingAccess && (
                            <div><strong>Total:</strong> <span style={{ color: '#004aad', fontWeight: 'bold' }}>{formatCurrency(procedure.totalPrice || (procedure.price * (procedure.quantity || 1)))}</span></div>
                          )}
                        </div>

                        <div className="outsource-procedure-timestamps">
                          <span style={{ marginRight: '15px' }}>
                            <strong>Added:</strong> {new Date(procedure.addedAt || caseData.createdAt).toLocaleString()}
                            {procedure.changeType === 'added' && procedure.addedBy?.labName && (
                              <span style={{ color: '#999' }}> (by {procedure.addedBy.labName})</span>
                            )}
                          </span>
                          {procedure.updatedAt && procedure.updatedAt !== procedure.addedAt && (
                            <span style={{ color: '#856404' }}>
                              <strong>Last updated:</strong> {new Date(procedure.updatedAt).toLocaleString()}
                              {procedure.addedBy?.labName && <span style={{ color: '#999' }}> (by {procedure.addedBy.labName})</span>}
                            </span>
                          )}
                          {procedure.changeType && (
                            <Badge bg={procedure.changeType === 'added' ? 'success' : procedure.changeType === 'quantity_updated' ? 'warning' : procedure.changeType === 'replaced' ? 'info' : 'secondary'} style={{ marginLeft: '10px', fontSize: '9px' }}>
                              {procedure.changeType.replace('_', ' ')}
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Grand Total — only when pricing access */}
                  {hasPricingAccess && procedureList.length > 0 && (
                    <div className="outsource-grand-total">
                      <div className="outsource-grand-total-inner">
                        <div>
                          <div style={{ fontSize: '13px', color: '#2e7d32' }}>
                            <strong>{procedureList.length} Procedure(s)</strong> | Total Quantity: {totalQuantity}
                          </div>
                          <div style={{ fontSize: '13px', color: '#2e7d32' }}>Subtotal: {formatCurrency(subtotal)}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '14px', color: '#1b5e20', fontWeight: '600' }}>Grand Total</div>
                          <div className="outsource-grand-total-amount">{formatCurrency(totalPrice)}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* When no pricing access: show a neutral summary line */}
                  {!hasPricingAccess && procedureList.length > 0 && (
                    <div className="outsource-grand-total">
                      <div className="outsource-grand-total-inner">
                        <div>
                          <div style={{ fontSize: '13px', color: '#2e7d32' }}>
                            <strong>{procedureList.length} Procedure(s)</strong> | Total Quantity: {totalQuantity}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Case Information */}
            <div className="outsource-case-info">
              <h4>Case Information</h4>
              <div className="outsource-case-info-grid">
                <p><strong>Case Number:</strong> {caseData.caseNumber}</p>
                {/* ✅ Re-outsourced case: show original case number too */}
                {caseData.isReOutsource && caseData.originalCaseNumber && (
                  <p>
                    <strong>Original Case Number:</strong> {caseData.originalCaseNumber}
                    <span style={{ marginLeft: '8px', fontSize: '11px', backgroundColor: '#cce5ff', color: '#004085', padding: '1px 8px', borderRadius: '10px' }}>
                      Re-Outsourced
                    </span>
                  </p>
                )}
                <p><strong>Created Date:</strong> {formatDate(caseData.createdAt)}</p>
                <p><strong>Required Date:</strong> {formatDate(caseData.requiredDate)}</p>
                <p><strong>Last Updated:</strong> {formatDate(caseData.updatedAt)}</p>
                <p><strong>Created By:</strong> {caseData.createdBy?.labName || 'Unknown'}</p>
                {caseData.notes && <p className="outsource-full-width"><strong>Notes:</strong> {caseData.notes}</p>}
              </div>
            </div>

            {/* Notes Section */}
            <div className="outsource-notes-section">
              <div className="outsource-notes-header">
                <h4><FaStickyNote /> Notes {notesHistory.length > 0 && `(${notesHistory.length})`}</h4>
                {isDestinationLab && (
                  <button onClick={() => setShowAddNoteModal(true)} className="outsource-add-note-btn">
                    <FaPlus /> Add Note
                  </button>
                )}
              </div>

              {loadingNotes ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Spinner animation="border" size="sm" style={{ color: '#004aad' }} />
                  <p style={{ marginTop: '10px', color: '#666', fontSize: '14px' }}>Loading notes...</p>
                </div>
              ) : notesHistory && notesHistory.length > 0 ? (
                <div className="outsource-notes-list">
                  {notesHistory.map((note, index) => (
                    <div key={note.noteId || note._id || index} className={`outsource-note-item ${note.addedBy?.labRole === 'destination' ? 'destination' : 'source'}`}>
                      <div className="outsource-note-header">
                        <div className="outsource-note-meta">
                          <strong style={{ color: '#004aad', fontSize: '14px' }}>{note.addedBy?.labName || 'Unknown Lab'}</strong>
                          <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px', backgroundColor: note.addedBy?.labRole === 'destination' ? '#d4edda' : '#fff3cd', color: note.addedBy?.labRole === 'destination' ? '#155724' : '#856404', fontWeight: '500' }}>
                            {note.addedBy?.labRole === 'destination' ? 'Destination Lab' : 'Source Lab'}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <small style={{ color: '#666', fontSize: '11px' }}>{new Date(note.createdAt).toLocaleString()}</small>
                          {isDestinationLab && note.addedBy?.labRole === 'destination' && !String(note.noteId || note._id).startsWith('initial-') && (
                            <button onClick={() => handleDeleteNote(note.noteId || note._id)} disabled={deletingNoteId === (note.noteId || note._id)} className="outsource-note-delete-btn" title="Delete this note">
                              {deletingNoteId === (note.noteId || note._id) ? <Spinner animation="border" size="sm" /> : <FaTrash size={12} />}
                            </button>
                          )}
                        </div>
                      </div>
                      <p style={{ margin: 0, color: '#333', fontSize: '14px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '1.5' }}>{note.content}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="outsource-empty-notes">
                  <FaStickyNote style={{ fontSize: '32px', marginBottom: '10px', color: '#ccc' }} />
                  <p style={{ margin: 0, fontSize: '14px' }}>No notes yet</p>
                  {isDestinationLab && <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#999' }}>Click "Add Note" to add the first note</p>}
                </div>
              )}
            </div>

            {/* File Change History Section */}
            <div className="outsource-file-history-section">
              <div className="outsource-file-history-header">
                <h4><FaHistory /> File Change History {fileChangeHistory.length > 0 && `(${fileChangeHistory.length})`}</h4>
                <Button variant="info" size="sm" onClick={openFileHistoryModal} className="outsource-view-history-btn">
                  <FaHistory style={{ marginRight: '5px' }} /> View Full History
                </Button>
              </div>

              {loadingFileHistory ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Spinner animation="border" size="sm" style={{ color: '#17a2b8' }} />
                  <p style={{ marginTop: '10px', color: '#666', fontSize: '14px' }}>Loading file history...</p>
                </div>
              ) : fileChangeHistory.length > 0 ? (
                <div className="outsource-file-history-list">
                  {fileChangeHistory.slice(0, 5).map((change, index) => (
                    <div key={index} className={`outsource-file-change-item ${change.changeType === 'file_deleted' ? 'deleted' : change.changeType === 'file_replaced' ? 'replaced' : 'uploaded'}`}>
                      <div className="outsource-file-change-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {change.changeType === 'file_deleted' && <FaTrash style={{ color: '#dc3545' }} />}
                          {change.changeType === 'file_replaced' && <FaExchangeAlt style={{ color: '#17a2b8' }} />}
                          {change.changeType === 'file_uploaded' && <FaUpload style={{ color: '#28a745' }} />}
                          {change.changeType === 'multiple_files_uploaded' && <FaCloudUploadAlt style={{ color: '#28a745' }} />}
                          <span style={{ fontSize: '13px', fontWeight: '500' }}>{change.description}</span>
                        </div>
                        <small style={{ color: '#666', fontSize: '11px' }}>{formatDateTime(change.changedAt)}</small>
                      </div>
                      <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                        By: {change.changedBy?.labName || 'Unknown'} ({change.changedBy?.userName || 'Unknown'})
                      </div>
                    </div>
                  ))}
                  {fileChangeHistory.length > 5 && (
                    <p style={{ textAlign: 'center', color: '#666', fontSize: '12px', margin: '10px 0 0 0' }}>+ {fileChangeHistory.length - 5} more changes. Click "View Full History" to see all.</p>
                  )}
                </div>
              ) : (
                <div className="outsource-empty-history">
                  <FaHistory style={{ fontSize: '24px', marginBottom: '8px', color: '#ccc' }} />
                  <p style={{ margin: 0, fontSize: '14px' }}>No file changes recorded yet</p>
                  <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#999' }}>File uploads, deletions, and replacements will be tracked here</p>
                </div>
              )}
            </div>

            {/* Original Case Files Section */}
            {originalFiles.length > 0 && (
              <>
                <div className="outsource-file-section-header">
                  <h4>Original Case Files ({originalFiles.length})</h4>
                </div>
                {renderFileSection('Intraoral Scan Files', originalFiles, 'stl', false, false)}
                {renderFileSection('Image Files', originalFiles, 'image', false, false)}
                {renderFileSection('Other Files', originalFiles, 'other', false, false)}
              </>
            )}

            {/* File Upload Section */}
            <div className="outsource-upload-section">
              <h4><FaCloudUploadAlt /> Upload Files</h4>

              {!canModifyFiles() && (
                <div className={`outsource-locked-warning ${caseData.status === 'completed' ? 'completed' : 'cancelled'}`}>
                  {caseData.status === 'completed' ? (
                    <><FaCheckCircle /><span><strong>Case Completed:</strong> Files are locked for record keeping. No changes allowed.</span></>
                  ) : (
                    <><FaBan /><span><strong>Case Cancelled:</strong> Cannot upload files to a cancelled case.</span></>
                  )}
                </div>
              )}

              <div className="outsource-upload-toggle">
                <button onClick={() => { setUploadMode('single'); setSelectedFiles([]); setUploadStatuses([]); setShowUploadSummary(false); }} className={uploadMode === 'single' ? 'active' : 'inactive'}>
                  <FaUpload /> Single File Upload
                </button>
                <button onClick={() => setUploadMode('multiple')} className={uploadMode === 'multiple' ? 'active' : 'inactive'}>
                  <FaCloudUploadAlt /> Multi-File Upload (Max 10)
                </button>
              </div>

              {uploadMode === 'single' && (
                <div className={`outsource-upload-area ${!canModifyFiles() ? 'disabled' : ''}`}>
                  <div className="outsource-upload-controls">
                    <input ref={fileInputRef} id="singleFileInput" type="file" onChange={handleSingleFileSelect} disabled={!canModifyFiles()} accept=".stl,.pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.zip,.rar,.obj,.ply,.3mf,.constructioninfo,.json,.xml" />
                    <Button variant="primary" onClick={handleUploadSingleFile} disabled={uploading || !selectedFile || !canModifyFiles()} className="outsource-upload-btn primary">
                      {uploading ? <><FaSpinner className="spinner-border spinner-border-sm me-2" />Uploading... {uploadProgress}%</> : <><FaUpload style={{ marginRight: '6px' }} /> Upload File</>}
                    </Button>
                  </div>
                  {selectedFile && <p className="outsource-selected-file-info">Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})</p>}
                  {uploading && <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} style={{ marginTop: '10px', height: '20px' }} />}
                </div>
              )}

              {uploadMode === 'multiple' && (
                <div>
                  <div className={`outsource-upload-area ${!canModifyFiles() ? 'disabled' : ''}`} style={{ marginBottom: '15px' }}>
                    <div className="outsource-upload-controls">
                      <input ref={multiFileInputRef} id="multiFileInput" type="file" multiple onChange={handleMultipleFileSelect} disabled={!canModifyFiles()} accept=".stl,.pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.zip,.rar,.obj,.ply,.3mf,.constructioninfo,.json,.xml" />
                      {selectedFiles.length > 0 && !uploadingMultiple && (
                        <>
                          <Button variant="primary" onClick={handleUploadMultipleFiles} disabled={!canModifyFiles()} className="outsource-upload-btn success">
                            <FaCloudUploadAlt style={{ marginRight: '6px' }} /> Upload All ({selectedFiles.length} files)
                          </Button>
                          <Button variant="outline-danger" onClick={clearSelectedFiles} className="outsource-upload-btn outline-danger">
                            <FaTimes style={{ marginRight: '6px' }} /> Clear
                          </Button>
                        </>
                      )}
                    </div>

                    {selectedFiles.length > 0 && (
                      <div className="outsource-file-list">
                        <p className="outsource-file-list-header">
                          <strong>{selectedFiles.length} file(s) selected</strong> (Total: {formatFileSize(selectedFiles.reduce((sum, f) => sum + f.size, 0))})
                        </p>
                        <div className="outsource-file-list-container">
                          {selectedFiles.map((file, index) => (
                            <div key={index} className="outsource-file-list-item">
                              <div className="outsource-file-list-item-info">
                                <FaFile style={{ color: '#004aad', fontSize: '16px' }} />
                                <span className="outsource-file-list-item-name">{file.name}</span>
                              </div>
                              <Badge bg="secondary" style={{ marginLeft: '10px', fontSize: '11px' }}>{formatFileSize(file.size)}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {showUploadSummary && uploadStatuses.length > 0 && (
                    <div className="outsource-upload-summary">
                      <h5>
                        Upload Progress
                        <Badge bg={uploadingMultiple ? 'warning' : 'success'}>{uploadingMultiple ? 'Uploading...' : 'Complete'}</Badge>
                      </h5>

                      {uploadingMultiple && <ProgressBar now={multiUploadProgress} label={`${multiUploadProgress}%`} style={{ marginBottom: '15px', height: '20px' }} />}

                      <div className="outsource-upload-status-list">
                        {uploadStatuses.map((status, index) => (
                          <div key={index} className="outsource-upload-status-item">
                            <div className="outsource-upload-status-icon">
                              {status.status === 'success' && <FaCheckCircle style={{ color: '#28a745' }} />}
                              {status.status === 'error' && <FaTimesCircle style={{ color: '#dc3545' }} />}
                              {status.status === 'uploading' && <FaSpinner className="spinner-border spinner-border-sm text-primary" />}
                              {status.status === 'pending' && <FaFile style={{ color: '#6c757d' }} />}
                            </div>
                            <div className="outsource-upload-status-info">
                              <div className="outsource-upload-status-name">{status.name}</div>
                              <div className="outsource-upload-status-meta">
                                {formatFileSize(status.size)}
                                {status.status === 'error' && status.error && <span style={{ color: '#dc3545', marginLeft: '10px' }}>- {status.error}</span>}
                              </div>
                              {status.status === 'uploading' && <ProgressBar now={status.progress} style={{ height: '4px', marginTop: '4px' }} variant="primary" />}
                            </div>
                            <Badge bg={status.status === 'success' ? 'success' : status.status === 'error' ? 'danger' : status.status === 'uploading' ? 'warning' : 'secondary'} style={{ fontSize: '10px' }}>
                              {status.status.charAt(0).toUpperCase() + status.status.slice(1)}
                            </Badge>
                          </div>
                        ))}
                      </div>

                      {!uploadingMultiple && (
                        <div className="outsource-upload-summary-stats">
                          <div className="outsource-upload-summary-stat"><FaCheckCircle style={{ color: '#28a745' }} /><strong>Success:</strong> {uploadStatuses.filter(s => s.status === 'success').length}</div>
                          <div className="outsource-upload-summary-stat"><FaTimesCircle style={{ color: '#dc3545' }} /><strong>Failed:</strong> {uploadStatuses.filter(s => s.status === 'error').length}</div>
                          <div className="outsource-upload-summary-stat"><FaFile style={{ color: '#6c757d' }} /><strong>Total:</strong> {uploadStatuses.length}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="outsource-file-types-info">
                <strong>Supported file types:</strong> STL, PDF, JPG, PNG, DOC, DOCX, XLS, XLSX, ZIP, RAR, OBJ, PLY, 3MF, ConstructionInfo, JSON, XML
                <br />
                <strong>Maximum file size:</strong> 100MB per file | <strong>Maximum files:</strong> 10 at once
              </div>
            </div>

            {/* Additional Uploaded Files Section */}
            {additionalFiles.length > 0 && (
              <>
                <div className="outsource-file-section-header">
                  <h4>Additional Uploaded Files ({additionalFiles.length})</h4>
                </div>
                {renderFileSection('Intraoral Scan Files', additionalFiles, 'stl', false, true)}
                {renderFileSection('Image Files', additionalFiles, 'image', false, true)}
                {renderFileSection('Other Files', additionalFiles, 'other', false, true)}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Add Note Modal */}
      <Modal show={showAddNoteModal} onHide={() => { setShowAddNoteModal(false); setNewNote(''); }} centered>
        <Modal.Header closeButton className="outsource-modal-header">
          <Modal.Title><FaStickyNote style={{ marginRight: '10px' }} />Add Note</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label style={{ fontWeight: '600', color: '#333' }}>Enter your note for case #{caseData?.caseNumber}</Form.Label>
            <Form.Control as="textarea" rows={5} value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Type your note here..." style={{ marginTop: '10px', padding: '12px', borderRadius: '5px', border: '1px solid #ccc', resize: 'vertical', fontSize: '14px' }} />
            <Form.Text className="text-muted outsource-form-text">This note will be visible to both the source and destination labs.</Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowAddNoteModal(false); setNewNote(''); }}>Cancel</Button>
          <Button variant="primary" onClick={handleAddNote} disabled={addingNote || !newNote.trim()} className="outsource-btn-primary">
            {addingNote ? <><Spinner animation="border" size="sm" className="me-2" />Adding...</> : 'Add Note'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Procedure Modal */}
      <Modal show={showAddProcedureModal} onHide={() => { setShowAddProcedureModal(false); setSelectedProcedureForAdd(null); setAddProcedureQuantity(1); setAddProcedureNotes(''); }} size="lg" centered>
        <Modal.Header closeButton className="outsource-modal-header green">
          <Modal.Title><FaPlus style={{ marginRight: '10px' }} />Add Procedure to Case #{caseData?.caseNumber}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="outsource-modal-body">
          {loadingAvailableProcedures ? (
            <div className="outsource-modal-loading"><Spinner animation="border" style={{ color: '#28a745' }} /><p style={{ marginTop: '10px', color: '#666' }}>Loading available procedures...</p></div>
          ) : availableProcedures.length === 0 ? (
            <div className="outsource-modal-empty"><p>No additional procedures available to add.</p><p style={{ fontSize: '13px', color: '#999' }}>All procedures from your outsource list have been added to this case.</p></div>
          ) : (
            <>
              <Form.Group className="outsource-form-group">
                <Form.Label className="outsource-form-label">Select Procedure</Form.Label>
                <Form.Select value={selectedProcedureForAdd?.procedureId || ''} onChange={(e) => { const selected = availableProcedures.find(p => p.procedureId === e.target.value); setSelectedProcedureForAdd(selected || null); }} className="outsource-form-control">
                  <option value="">-- Select a procedure --</option>
                  {availableProcedures.map(proc => (
                    <option key={proc.procedureId} value={proc.procedureId}>
                      {/* ✅ Price only when hasPricingAccess */}
                      {proc.procedureName}
                      {hasPricingAccess ? ` - ${formatCurrency(proc.outsourcePrice)}` : ''}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {selectedProcedureForAdd && (
                <div className="outsource-modal-info-box gray">
                  <p style={{ margin: '5px 0', fontSize: '14px' }}><strong>Category:</strong> {selectedProcedureForAdd.category || 'N/A'}</p>
                  {hasPricingAccess && (
                    <p style={{ margin: '5px 0', fontSize: '14px' }}><strong>Price:</strong> {formatCurrency(selectedProcedureForAdd.outsourcePrice)}</p>
                  )}
                  {selectedProcedureForAdd.extDesc && <p style={{ margin: '5px 0', fontSize: '13px', color: '#666' }}><strong>Description:</strong> {selectedProcedureForAdd.extDesc}</p>}
                </div>
              )}

              <Form.Group className="outsource-form-group">
                <Form.Label className="outsource-form-label">Quantity</Form.Label>
                <Form.Control type="number" min="1" value={addProcedureQuantity} onChange={(e) => setAddProcedureQuantity(parseInt(e.target.value) || 1)} className="outsource-form-control" />
              </Form.Group>

              <Form.Group>
                <Form.Label className="outsource-form-label">Notes (Optional)</Form.Label>
                <Form.Control as="textarea" rows={2} value={addProcedureNotes} onChange={(e) => setAddProcedureNotes(e.target.value)} placeholder="Add notes about this procedure..." className="outsource-form-control" />
              </Form.Group>

              {selectedProcedureForAdd && hasPricingAccess && (
                <div className="outsource-modal-info-box green">
                  <p style={{ margin: 0, fontSize: '14px', fontWeight: 'bold' }}>Will add: {addProcedureQuantity}x {selectedProcedureForAdd.procedureName} = {formatCurrency(addProcedureQuantity * selectedProcedureForAdd.outsourcePrice)}</p>
                </div>
              )}
              {selectedProcedureForAdd && !hasPricingAccess && (
                <div className="outsource-modal-info-box green">
                  <p style={{ margin: 0, fontSize: '14px', fontWeight: 'bold' }}>Will add: {addProcedureQuantity}x {selectedProcedureForAdd.procedureName}</p>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowAddProcedureModal(false); setSelectedProcedureForAdd(null); setAddProcedureQuantity(1); setAddProcedureNotes(''); }}>Cancel</Button>
          <Button variant="success" onClick={handleAddProcedure} disabled={addingProcedure || !selectedProcedureForAdd || addProcedureQuantity < 1}>
            {addingProcedure ? <><Spinner animation="border" size="sm" className="me-2" />Adding...</> : <><FaPlus style={{ marginRight: '6px' }} /> Add Procedure</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Replace Procedure Modal */}
      <Modal show={showReplaceProcedureModal} onHide={() => { setShowReplaceProcedureModal(false); setReplacingProcedure(null); setSelectedReplacementProcedure(null); setReplacementQuantity(1); setReplacementNotes(''); }} size="lg" centered>
        <Modal.Header closeButton className="outsource-modal-header yellow">
          <Modal.Title><FaExchangeAlt style={{ marginRight: '10px' }} />Replace Procedure in Case #{caseData?.caseNumber}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="outsource-modal-body">
          {replacingProcedure && (
            <div className="outsource-modal-info-box yellow">
              <p style={{ margin: '5px 0', fontSize: '14px' }}><strong>Replacing:</strong> {replacingProcedure.procedureName}</p>
              <p style={{ margin: '5px 0', fontSize: '14px' }}><strong>Current Quantity:</strong> {replacingProcedure.quantity}</p>
              {hasPricingAccess && (
                <p style={{ margin: '5px 0', fontSize: '14px' }}><strong>Current Total:</strong> {formatCurrency(replacingProcedure.totalPrice)}</p>
              )}
            </div>
          )}

          {loadingReplacementProcedures ? (
            <div className="outsource-modal-loading"><Spinner animation="border" style={{ color: '#ffc107' }} /><p style={{ marginTop: '10px', color: '#666' }}>Loading available procedures...</p></div>
          ) : availableReplacementProcedures.length === 0 ? (
            <div className="outsource-modal-empty"><p>No replacement procedures available.</p><p style={{ fontSize: '13px', color: '#999' }}>All available procedures are either already in this case or none are available.</p></div>
          ) : (
            <>
              <Form.Group className="outsource-form-group">
                <Form.Label className="outsource-form-label">Select Replacement Procedure</Form.Label>
                <Form.Select value={selectedReplacementProcedure?.procedureId || ''} onChange={(e) => { const selected = availableReplacementProcedures.find(p => p.procedureId === e.target.value); setSelectedReplacementProcedure(selected || null); }} className="outsource-form-control">
                  <option value="">-- Select a replacement procedure --</option>
                  {availableReplacementProcedures.map(proc => (
                    <option key={proc.procedureId} value={proc.procedureId}>
                      {proc.procedureName}
                      {hasPricingAccess ? ` - ${formatCurrency(proc.outsourcePrice)}` : ''}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {selectedReplacementProcedure && (
                <div className="outsource-modal-info-box gray">
                  <p style={{ margin: '5px 0', fontSize: '14px' }}><strong>Category:</strong> {selectedReplacementProcedure.category || 'N/A'}</p>
                  {hasPricingAccess && (
                    <p style={{ margin: '5px 0', fontSize: '14px' }}><strong>Price:</strong> {formatCurrency(selectedReplacementProcedure.outsourcePrice)}</p>
                  )}
                  {selectedReplacementProcedure.extDesc && <p style={{ margin: '5px 0', fontSize: '13px', color: '#666' }}><strong>Description:</strong> {selectedReplacementProcedure.extDesc}</p>}
                </div>
              )}

              <Form.Group className="outsource-form-group">
                <Form.Label className="outsource-form-label">Quantity</Form.Label>
                <Form.Control type="number" min="1" value={replacementQuantity} onChange={(e) => setReplacementQuantity(parseInt(e.target.value) || 1)} className="outsource-form-control" />
              </Form.Group>

              <Form.Group>
                <Form.Label className="outsource-form-label">Notes (Optional)</Form.Label>
                <Form.Control as="textarea" rows={2} value={replacementNotes} onChange={(e) => setReplacementNotes(e.target.value)} placeholder="Reason for replacement..." className="outsource-form-control" />
              </Form.Group>

              {selectedReplacementProcedure && hasPricingAccess && (
                <div className="outsource-modal-info-box green">
                  <p style={{ margin: 0, fontSize: '14px', fontWeight: 'bold' }}>Will replace with: {replacementQuantity}x {selectedReplacementProcedure.procedureName} = {formatCurrency(replacementQuantity * selectedReplacementProcedure.outsourcePrice)}</p>
                  {replacingProcedure && <p style={{ margin: '5px 0 0 0', fontSize: '13px', color: '#666' }}>(Removing: {replacingProcedure.quantity}x {replacingProcedure.procedureName} = {formatCurrency(replacingProcedure.totalPrice)})</p>}
                </div>
              )}
              {selectedReplacementProcedure && !hasPricingAccess && (
                <div className="outsource-modal-info-box green">
                  <p style={{ margin: 0, fontSize: '14px', fontWeight: 'bold' }}>Will replace with: {replacementQuantity}x {selectedReplacementProcedure.procedureName}</p>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowReplaceProcedureModal(false); setReplacingProcedure(null); setSelectedReplacementProcedure(null); setReplacementQuantity(1); setReplacementNotes(''); }}>Cancel</Button>
          <Button variant="warning" onClick={handleReplaceProcedure} disabled={replacingProcedureLoading || !selectedReplacementProcedure || replacementQuantity < 1} style={{ color: '#333' }}>
            {replacingProcedureLoading ? <><Spinner animation="border" size="sm" className="me-2" />Replacing...</> : <><FaExchangeAlt style={{ marginRight: '6px' }} /> Replace Procedure</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* File Change History Modal */}
      <Modal show={showFileHistoryModal} onHide={() => setShowFileHistoryModal(false)} size="lg" centered>
        <Modal.Header closeButton className="outsource-modal-header cyan">
          <Modal.Title><FaHistory style={{ marginRight: '10px' }} />File Change History - Case #{caseData?.caseNumber}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="outsource-modal-body">
          {loadingFileHistory ? (
            <div className="outsource-modal-loading"><Spinner animation="border" style={{ color: '#17a2b8' }} /><p style={{ marginTop: '10px', color: '#666' }}>Loading file history...</p></div>
          ) : fileChangeHistory.length === 0 ? (
            <div className="outsource-modal-empty"><FaHistory style={{ fontSize: '32px', marginBottom: '10px', color: '#ccc' }} /><p>No file changes recorded yet</p></div>
          ) : (
            <div>
              {fileChangeHistory.map((change, index) => (
                <div key={index} className={`outsource-history-item ${change.changeType === 'file_deleted' ? 'deleted' : change.changeType === 'file_replaced' ? 'replaced' : change.changeType === 'multiple_files_uploaded' ? 'multiple' : 'uploaded'}`}>
                  <div className="outsource-history-header">
                    <div className="outsource-history-type">
                      {change.changeType === 'file_deleted' && <FaTrash style={{ color: '#dc3545', fontSize: '18px' }} />}
                      {change.changeType === 'file_replaced' && <FaExchangeAlt style={{ color: '#17a2b8', fontSize: '18px' }} />}
                      {change.changeType === 'file_uploaded' && <FaUpload style={{ color: '#28a745', fontSize: '18px' }} />}
                      {change.changeType === 'multiple_files_uploaded' && <FaCloudUploadAlt style={{ color: '#28a745', fontSize: '18px' }} />}
                      <Badge bg={change.changeType === 'file_deleted' ? 'danger' : change.changeType === 'file_replaced' ? 'info' : 'success'}>
                        {change.changeType.replace(/_/g, ' ').toUpperCase()}
                      </Badge>
                    </div>
                    <small style={{ color: '#666' }}>{formatDateTime(change.changedAt)}</small>
                  </div>

                  <p style={{ margin: '0 0 10px 0', fontWeight: '500', color: '#333' }}>{change.description}</p>

                  {change.changeType === 'file_deleted' && change.deletedFile && (
                    <div className="outsource-history-details">
                      <p><strong>File Name:</strong> {change.deletedFile.originalName || change.deletedFile.fileName}</p>
                      <p><strong>File Type:</strong> {change.deletedFile.fileType}</p>
                      <p><strong>File Size:</strong> {formatFileSize(change.deletedFile.fileSize)}</p>
                      <p><strong>Originally Uploaded:</strong> {change.deletedFile.uploadedAt ? formatDateTime(change.deletedFile.uploadedAt) : 'N/A'}</p>
                      {change.deletedFile.uploadedBy && <p><strong>Uploaded By:</strong> {change.deletedFile.uploadedBy.userName} ({change.deletedFile.uploadedBy.labName})</p>}
                    </div>
                  )}

                  {change.changeType === 'file_replaced' && (
                    <div className="outsource-history-details">
                      <div style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px dashed #ccc' }}>
                        <p style={{ color: '#dc3545' }}><strong>❌ Old File:</strong> {change.replacedFile?.originalName}</p>
                        <p style={{ fontSize: '11px', color: '#666' }}>Size: {formatFileSize(change.replacedFile?.fileSize)} | Uploaded: {change.replacedFile?.uploadedAt ? formatDateTime(change.replacedFile.uploadedAt) : 'N/A'}</p>
                      </div>
                      <div>
                        <p style={{ color: '#28a745' }}><strong>✅ New File:</strong> {change.newFile?.originalName}</p>
                        <p style={{ fontSize: '11px', color: '#666' }}>Size: {formatFileSize(change.newFile?.fileSize)} | Uploaded: {change.newFile?.uploadedAt ? formatDateTime(change.newFile.uploadedAt) : 'N/A'}</p>
                      </div>
                    </div>
                  )}

                  {(change.changeType === 'file_uploaded' || change.changeType === 'multiple_files_uploaded') && (
                    <div className="outsource-history-details">
                      {change.changeType === 'file_uploaded' && change.newFile && (
                        <>
                          <p><strong>File Name:</strong> {change.newFile.originalName}</p>
                          <p><strong>File Type:</strong> {change.newFile.fileType}</p>
                          <p><strong>File Size:</strong> {formatFileSize(change.newFile.fileSize)}</p>
                        </>
                      )}
                      {change.changeType === 'multiple_files_uploaded' && change.uploadedFiles && (
                        <>
                          <p><strong>Files Uploaded ({change.fileCount}):</strong></p>
                          <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                            {change.uploadedFiles.map((f, i) => (
                              <li key={i} style={{ fontSize: '12px' }}>{f.originalName} ({formatFileSize(f.fileSize)})</li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  )}

                  <div className="outsource-history-footer">
                    <span><strong>Changed By:</strong> {change.changedBy?.userName || 'Unknown'} ({change.changedBy?.labName || 'Unknown'})</span>
                    <Badge bg={change.changedBy?.labRole === 'source' ? 'primary' : 'secondary'}>{change.changedBy?.labRole === 'source' ? 'Source Lab' : 'Destination Lab'}</Badge>
                  </div>

                  {change.notes && <div className="outsource-history-notes"><strong>Notes:</strong> {change.notes}</div>}
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFileHistoryModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Replace File Modal */}
      <Modal show={showReplaceFileModal} onHide={() => { setShowReplaceFileModal(false); setFileToReplace(null); setReplacementFile(null); setReplacementFileNotes(''); }} centered>
        <Modal.Header closeButton className="outsource-modal-header yellow">
          <Modal.Title><FaExchangeAlt style={{ marginRight: '10px' }} />Replace File</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {fileToReplace && (
            <div className="outsource-modal-info-box yellow">
              <p style={{ margin: '5px 0', fontSize: '14px' }}><strong>Replacing:</strong> {fileToReplace.originalName || fileToReplace.fileName}</p>
              <p style={{ margin: '5px 0', fontSize: '13px', color: '#666' }}><strong>File Type:</strong> {fileToReplace.fileType}</p>
              <p style={{ margin: '5px 0', fontSize: '13px', color: '#666' }}><strong>Size:</strong> {formatFileSize(fileToReplace.fileSize)}</p>
            </div>
          )}

          <Form.Group className="outsource-form-group">
            <Form.Label className="outsource-form-label">Select Replacement File</Form.Label>
            <Form.Control ref={replaceFileInputRef} type="file" onChange={handleReplacementFileSelect} accept=".stl,.pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.zip,.rar,.obj,.ply,.3mf,.constructioninfo,.json,.xml" className="outsource-form-control" />
            {replacementFile && <Form.Text className="text-muted outsource-form-text">Selected: {replacementFile.name} ({formatFileSize(replacementFile.size)})</Form.Text>}
          </Form.Group>

          <Form.Group>
            <Form.Label className="outsource-form-label">Notes (Optional)</Form.Label>
            <Form.Control as="textarea" rows={2} value={replacementFileNotes} onChange={(e) => setReplacementFileNotes(e.target.value)} placeholder="Reason for replacement..." className="outsource-form-control" />
          </Form.Group>

          <div className="outsource-modal-info-box yellow" style={{ marginTop: '15px' }}>
            <strong>Note:</strong> The old file will be permanently deleted and this change will be recorded in the case history.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowReplaceFileModal(false); setFileToReplace(null); setReplacementFile(null); setReplacementFileNotes(''); }}>Cancel</Button>
          <Button variant="warning" onClick={handleReplaceFile} disabled={replacingFile || !replacementFile} style={{ color: '#333' }}>
            {replacingFile ? <><Spinner animation="border" size="sm" className="me-2" />Replacing...</> : <><FaExchangeAlt style={{ marginRight: '6px' }} /> Replace File</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Outsource Invoice Modal */}
      {hasPricingAccess && (
        <OutsourceInvoiceModal show={showInvoiceModal} onHide={() => setShowInvoiceModal(false)} caseData={caseData} onSuccess={handleInvoiceSuccess} />
      )}

      {/* ✅ STL Viewer Modal — now uses the professional STLFullViewModal matching CaseTable */}
      {showSTLModal && selectedSTLFile && (
        <STLFullViewModal
          file={selectedSTLFileData}
          fileUrl={selectedSTLFile}
          onClose={() => {
            setShowSTLModal(false);
            setSelectedSTLFile(null);
            setSelectedSTLFileData(null);
          }}
          getResponsiveFontSize={getResponsiveFontSize}
        />
      )}

      {/* Image Modal */}
      {showImage && (
        <div className="outsource-image-modal" onClick={() => setShowImage(null)}>
          <img src={showImage} alt="Fullscreen" onError={(e) => { e.target.src = '/placeholder.png'; }} />
        </div>
      )}

      <Footer />
    </>
  );
};

export default OutsourceCaseDetails;